/**
 * Help registry: THE source of truth for help content writers.
 *
 * Every console page that has help: its pageKey, app, title, route (":id" style template for detail
 * pages), menu path (real menu labels), who can open it (from the real permission checks), what it
 * is for, its tabs (tabKey, title, purpose) and every tour anchor on it (data-help-id, what it is,
 * and the tab it lives in when it is inside a tab).
 *
 * Tour steps: `target` is an anchor id below (or empty for a centred card). When the anchor lives in
 * a tab, set the step route to "?tab={tab}" so the tour opens that tab first. Pages that support
 * ?tab= say so in their tab list.
 *
 * Keep this file accurate to the UI: when a page changes, update its entry and anchors.
 */

import type { HelpAppDef, HelpPageDef, HelpAnchorDef } from './helpRegistryTypes';

export const HELP_APPS: HelpAppDef[] = [
  { appKey: 'events', title: 'Events', basePath: '/app/events' },
  { appKey: 'rides', title: 'Carpool', basePath: '/app/rides' },
  { appKey: 'hr', title: 'HR', basePath: '/app/members' },
  { appKey: 'procedures', title: 'Procedures', basePath: '/app/procedures' },
  { appKey: 'members', title: 'Members', basePath: '/admin/members' },
  { appKey: 'callcentre', title: 'Call campaigns', basePath: '/app/callcentre' },
  // The volunteers' caller portal: a separate site (frontend/ccms_callcentre), not a console route. See CALLCENTRE_PORTAL_PAGES.
  { appKey: 'callcentre-portal', title: 'Caller portal', basePath: 'https://callcentre.actschurch.co.za' },
  { appKey: 'communications', title: 'Communications', basePath: '/admin/communications' },
  { appKey: 'forms', title: 'Forms', basePath: '/app/forms' },
];

/**
 * Anchors every page with help has: the "?" button and the header actions. Tabs have no "?" of their own (one "?"
 * per page; its menu has "Help for the {tab} tab"), so `tabs` adds no anchors and is kept only for existing callers.
 */
function common(pageKey: string, options: { actions?: boolean; tabs?: string[] } = {}): HelpAnchorDef[] {
  const out: HelpAnchorDef[] = [{ id: `${pageKey}.help`, what: 'The ? help button at the top right (tours, page help, help for the open tab and Ask AI)' }];
  if (options.actions !== false) out.push({ id: `${pageKey}.actions`, what: 'The page actions at the top right of the header' });
  return out;
}

const EVENTS_VIEW = 'Anyone who can open Events: Admin, EventsAdmin, Events Access or Events Coordinator role, or the Event.View right';

// ====================================================================== Events (/app/events)

const EVENTS_PAGES: HelpPageDef[] = [
  {
    pageKey: 'events.dashboard',
    appKey: 'events',
    title: 'Events dashboard',
    route: '/app/events',
    menuPath: 'Events › Overview › Dashboard',
    accessNote: EVENTS_VIEW,
    purpose: 'The church calendar at a glance: the next 30 days, events held for review and the health of the Planning Center sync.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('events.dashboard'),
      { id: 'events.dashboard.new-event', what: 'New event button (needs Event.Create)' },
      { id: 'events.dashboard.stat-upcoming', what: 'Next 30 days tile: events coming up; opens the events list' },
      { id: 'events.dashboard.stat-review', what: 'Held for review tile: synced events waiting for a type or place; opens Review' },
      { id: 'events.dashboard.stat-sync', what: 'Planning Center sync tile: when the last sync ran; opens Sources' },
      { id: 'events.dashboard.stat-problems', what: 'Sync problems tile: failed or partial runs in the last 10; opens Sources' },
      { id: 'events.dashboard.upcoming', what: 'Next 30 days card: every upcoming event, including held ones; click one to open it' },
      { id: 'events.dashboard.planning-center', what: 'Planning Center card: last sync status, summary and last error' },
      { id: 'events.dashboard.recent-errors', what: 'Recent errors card: failed or partial sync runs (only with source access and a source set up)' },
    ],
  },
  {
    pageKey: 'events.events-list',
    appKey: 'events',
    title: 'Events',
    route: '/app/events/events',
    menuPath: 'Events › Events › Events',
    accessNote: EVENTS_VIEW,
    purpose: 'Every event from CCMS and Planning Center as a paged list or a month calendar, with filters.',
    sortOrder: 20,
    tabs: [
      { key: 'list', title: 'List', purpose: 'Paged list with source, review status, coordinator and suggested links. ?tab=list (default).' },
      { key: 'calendar', title: 'Calendar', purpose: 'Month calendar of the events that match the filters; click a day to add an event. ?tab=calendar (or ?view=calendar).' },
    ],
    anchors: [
      ...common('events.events-list', { tabs: ['list', 'calendar'] }),
      { id: 'events.events-list.view-switch', what: 'List or Calendar switch' },
      { id: 'events.events-list.new-event', what: 'New event button (needs Event.Create)' },
      { id: 'events.events-list.filters', what: 'Filters: search, date range (list only), source, review status, status, Show deleted and Clear filters' },
      { id: 'events.events-list.list', what: 'The events table; click a row to open the event', tab: 'list' },
      { id: 'events.events-list.calendar', what: 'The month calendar; click an event to open it', tab: 'calendar' },
    ],
  },
  {
    pageKey: 'events.event-form',
    appKey: 'events',
    title: 'New or edit event',
    route: '/app/events/events/new',
    menuPath: 'Events › Events › Events › New event (edit: open an event, then Edit)',
    accessNote: 'Creating needs Event.Create; editing needs Event.Update (Admin and EventsAdmin have both)',
    purpose: 'Create a manual event or change one. Synced events keep their name, dates and description until they are detached.',
    sortOrder: 30,
    tabs: [],
    anchors: [
      ...common('events.event-form'),
      { id: 'events.event-form.details', what: 'Event card: name, type, status and description' },
      { id: 'events.event-form.when', what: 'When card: all day, start and end' },
      { id: 'events.event-form.where', what: 'Where card: campus (or all campuses) and venue' },
      { id: 'events.event-form.visibility', what: 'Visibility card: Public and Show in Carpool' },
      { id: 'events.event-form.save', what: 'Cancel and Create event or Save changes buttons' },
    ],
  },
  {
    pageKey: 'events.event-detail',
    appKey: 'events',
    title: 'Event',
    route: '/app/events/events/:id',
    menuPath: 'Events › Events › Events, then open an event',
    accessNote: `${EVENTS_VIEW}. Links need Event.Link.Manage or being the event coordinator; plans need Event.Plan.Approve, Event.Playbook.Manage or being the coordinator; Suggest with AI needs Event.AI.Use`,
    purpose: 'One event: its schedule, type, place and visibility. Who and what it is connected to. The plan of work it needs.',
    sortOrder: 40,
    tabs: [
      { key: 'details', title: 'Details', purpose: 'Schedule, type and place, visibility switches and where the event came from. Default tab.' },
      { key: 'connected', title: 'Connected', purpose: 'Suggested and approved links (departments, ministries, venues, people), work items, the coordinator and tags. ?tab=connected.' },
      { key: 'plan', title: 'Plan', purpose: 'Turn a playbook into a draft plan, tick and adjust the items, approve it to create the work in other apps, then follow progress. ?tab=plan.' },
    ],
    anchors: [
      ...common('events.event-detail', { tabs: ['details', 'connected', 'plan'] }),
      { id: 'events.event-detail.edit', what: 'Edit button (needs Event.Update)' },
      { id: 'events.event-detail.badges', what: 'Source, review and status badges under the title' },
      { id: 'events.event-detail.tab-details', what: 'Details tab button' },
      { id: 'events.event-detail.tab-connected', what: 'Connected tab button (shows the number of suggestions)' },
      { id: 'events.event-detail.tab-plan', what: 'Plan tab button' },
      { id: 'events.event-detail.schedule', what: 'Schedule card: when, all day, start, end and description', tab: 'details' },
      { id: 'events.event-detail.type-place', what: 'Type and place card: type, campus and venue ("Set here" means a sync keeps it)', tab: 'details' },
      { id: 'events.event-detail.visibility', what: 'Visibility card: Public and Show in Carpool switches', tab: 'details' },
      { id: 'events.event-detail.source', what: 'Source card: where the event came from and when it was last seen', tab: 'details' },
      { id: 'events.event-detail.suggestions', what: 'Suggestions card: approve or dismiss suggested links or use Approve all (only when there are suggestions)', tab: 'connected' },
      { id: 'events.event-detail.connected', what: 'Connected card: the approved links grouped by departments, ministries, venues and people', tab: 'connected' },
      { id: 'events.event-detail.mark-reviewed', what: 'Mark as reviewed button (until the links are reviewed)', tab: 'connected' },
      { id: 'events.event-detail.suggest-ai', what: 'Suggest with AI button (Event.AI.Use, when AI suggestions are on)', tab: 'connected' },
      { id: 'events.event-detail.add-link', what: 'Add link button', tab: 'connected' },
      { id: 'events.event-detail.work-items', what: 'Work card: tasks, projects, meetings, bookings, budgets, purchase requests and announcements linked to the event', tab: 'connected' },
      { id: 'events.event-detail.coordinator', what: 'Coordinator card: who leads the event', tab: 'connected' },
      { id: 'events.event-detail.tags', what: 'Tags card: CCMS and source tags on the event', tab: 'connected' },
      { id: 'events.event-detail.no-plan', what: 'No plan yet card with Start an empty plan (only before the first plan)', tab: 'plan' },
      { id: 'events.event-detail.plan', what: 'The current plan: the draft checklist or, once approved, live progress', tab: 'plan' },
      { id: 'events.event-detail.add-item', what: 'Add item button on a draft plan', tab: 'plan' },
      { id: 'events.event-detail.approve-plan', what: 'Approve plan button on a draft plan (creates the work as you)', tab: 'plan' },
      { id: 'events.event-detail.suggested-playbooks', what: 'Suggested playbooks card: create a plan from a suggestion, another playbook or an empty plan', tab: 'plan' },
      { id: 'events.event-detail.plans', what: 'Plans card: earlier plans for this event', tab: 'plan' },
    ],
  },
  {
    pageKey: 'events.review',
    appKey: 'events',
    title: 'Review',
    route: '/app/events/review',
    menuPath: 'Events › Events › Review',
    accessNote: 'Event.Review right (Admin and EventsAdmin have it)',
    purpose: 'Synced events held because a location or tag is not mapped yet. Pick a type and a place to release them to the calendar, Carpool and the shop.',
    sortOrder: 50,
    tabs: [],
    anchors: [
      ...common('events.review'),
      { id: 'events.review.mappings-link', what: 'Mappings button (map a value once for every event)' },
      { id: 'events.review.queue', what: 'The list of held events' },
      { id: 'events.review.first-card', what: 'The first held event' },
      { id: 'events.review.reasons', what: 'Why it is held: the unmapped location texts and tags (first event)' },
      { id: 'events.review.fix', what: 'Type, Remember for Planning Center, campus and venue fields (first event)' },
      { id: 'events.review.remember', what: 'Remember for Planning Center switch: saves the choice as a mapping (first event)' },
      { id: 'events.review.resolve', what: 'Resolve button (first event)' },
    ],
  },
  {
    pageKey: 'events.sources',
    appKey: 'events',
    title: 'Sources',
    route: '/app/events/sources',
    menuPath: 'Events › Planning Center › Sources',
    accessNote: 'Event.Source.View to view (Events Access has it); Event.Source.Manage to change settings; Event.Source.Sync for Test connection and Sync now',
    purpose: 'The Planning Center connection: switch syncing on, set the sync window and safeguards, test it, sync now and read the run history.',
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...common('events.sources'),
      { id: 'events.sources.mappings-link', what: 'Mappings button' },
      { id: 'events.sources.connection', what: 'The Planning Center connection card with its settings' },
      { id: 'events.sources.test-connection', what: 'Test connection button (Event.Source.Sync)' },
      { id: 'events.sources.sync-now', what: 'Sync now button (Event.Source.Sync)' },
      { id: 'events.sources.enabled', what: 'Enabled switch: sync on a schedule' },
      { id: 'events.sources.settings', what: 'Days ahead, days behind, sync every, misses before cancel, max missing % and default type' },
      { id: 'events.sources.save', what: 'Undo changes and Save buttons (Event.Source.Manage)' },
      { id: 'events.sources.last-run', what: 'Last run card: status, when, summary and last error' },
      { id: 'events.sources.run-history', what: 'Run history table' },
    ],
  },
  {
    pageKey: 'events.mappings',
    appKey: 'events',
    title: 'Mappings',
    route: '/app/events/mappings',
    menuPath: 'Events › Planning Center › Mappings',
    accessNote: 'Event.Source.View to view; Event.Mapping.Manage to change mappings',
    purpose: 'What the location texts and tags from Planning Center mean in CCMS. Map a value once and every event that uses it follows.',
    sortOrder: 70,
    tabs: [
      { key: 'locations', title: 'Locations', purpose: 'Location texts mapped to a campus and venue, All campuses, Ignore or Exclude events. Default tab.' },
      { key: 'tags', title: 'Tags', purpose: 'Planning Center tags mapped to an event type with a Carpool default, Ignore or Exclude events. ?tab=tags.' },
    ],
    anchors: [
      ...common('events.mappings', { tabs: ['locations', 'tags'] }),
      { id: 'events.mappings.review-link', what: 'Review button (needs Event.Review)' },
      { id: 'events.mappings.tab-locations', what: 'Locations tab button (orange count = unmapped)' },
      { id: 'events.mappings.tab-tags', what: 'Tags tab button (orange count = unmapped)' },
      { id: 'events.mappings.status-filter', what: 'All, Unmapped, Mapped, Ignored and Excluded filter' },
      { id: 'events.mappings.table', what: 'Mappings table: seen, held events, status, maps to, suggestion with Accept; click a row to change it' },
    ],
  },
  {
    pageKey: 'events.rules',
    appKey: 'events',
    title: 'Rules',
    route: '/app/events/rules',
    menuPath: 'Events › Automation › Rules',
    accessNote: 'Event.Rule.Manage right (Admin and EventsAdmin have it)',
    purpose: 'Rules suggest links (or apply them with AutoApply), set the coordinator or suggest a playbook for upcoming events that match.',
    sortOrder: 80,
    tabs: [],
    anchors: [
      ...common('events.rules'),
      { id: 'events.rules.re-evaluate', what: 'Re-evaluate upcoming events button' },
      { id: 'events.rules.new-rule', what: 'New rule button' },
      { id: 'events.rules.table', what: 'Rules table with the on and off switch; click a row to edit' },
    ],
  },
  {
    pageKey: 'events.rule-editor',
    appKey: 'events',
    title: 'Rule',
    route: '/app/events/rules/new',
    menuPath: 'Events › Automation › Rules › New rule (or click a rule)',
    accessNote: 'Event.Rule.Manage right (Admin and EventsAdmin have it)',
    purpose: 'Build a rule: conditions that must all match, the actions it takes, a dry-run preview and AutoApply.',
    sortOrder: 90,
    tabs: [],
    anchors: [
      ...common('events.rule-editor'),
      { id: 'events.rule-editor.preview', what: 'Preview button: a dry run over upcoming events' },
      { id: 'events.rule-editor.save', what: 'Create rule or Save button' },
      { id: 'events.rule-editor.conditions', what: 'When an event matches card: conditions (all must match) and Add condition' },
      { id: 'events.rule-editor.then', what: 'Then card: actions (add a link, set the coordinator, suggest a playbook) and Add action' },
      { id: 'events.rule-editor.preview-results', what: 'Preview results: which upcoming events match' },
      { id: 'events.rule-editor.settings', what: 'Rule card: name, description, priority, Switched on and AutoApply' },
      { id: 'events.rule-editor.auto-apply', what: 'AutoApply switch: apply links without review (asks first, with the number of events)' },
    ],
  },
  {
    pageKey: 'events.learning',
    appKey: 'events',
    title: 'Learning',
    route: '/app/events/learning',
    menuPath: 'Events › Automation › Learning',
    accessNote: 'Event.Learning.Review right (Admin and EventsAdmin have it)',
    purpose: 'Rule proposals CCMS learned from the links people approved. Accept one to create a rule; tune how sure CCMS must be; switch AI suggestions on.',
    sortOrder: 100,
    tabs: [],
    anchors: [
      ...common('events.learning'),
      { id: 'events.learning.status-filter', what: 'Proposed, Snoozed, Accepted and Rejected filter' },
      { id: 'events.learning.proposal', what: 'The first proposal: the pattern and the links with their confidence' },
      { id: 'events.learning.decide', what: 'Accept, Snooze and Reject buttons (first proposal)' },
      { id: 'events.learning.settings', what: 'Settings card: minimum support, minimum confidence, window and AI suggestions' },
      { id: 'events.learning.ai-suggestions', what: 'AI suggestions switch (shows Suggest with AI on events)' },
    ],
  },
  {
    pageKey: 'events.tags',
    appKey: 'events',
    title: 'Tags',
    route: '/app/events/tags',
    menuPath: 'Events › Automation › Tags',
    accessNote: 'Event.Tag.Manage right (Admin and EventsAdmin have it)',
    purpose: 'CCMS tags you create and set on events, next to the read-only tags from Planning Center. Rules and learning look at both.',
    sortOrder: 110,
    tabs: [],
    anchors: [
      ...common('events.tags'),
      { id: 'events.tags.new-tag', what: 'New tag button' },
      { id: 'events.tags.search', what: 'Search tags or groups' },
      { id: 'events.tags.ccms-tags', what: 'CCMS tags card (editable)' },
      { id: 'events.tags.source-tags', what: 'From the sources card (read-only, change them in Planning Center)' },
    ],
  },
  {
    pageKey: 'events.playbooks',
    appKey: 'events',
    title: 'Playbooks',
    route: '/app/events/playbooks',
    menuPath: 'Events › Automation › Playbooks',
    accessNote: 'Event.Playbook.Manage right (Admin and EventsAdmin have it)',
    purpose: 'Reusable lists of the work an event needs. On an event, a playbook becomes a plan with real dates and people.',
    sortOrder: 120,
    tabs: [],
    anchors: [
      ...common('events.playbooks'),
      { id: 'events.playbooks.new-playbook', what: 'New playbook button' },
      { id: 'events.playbooks.table', what: 'Playbooks table with the active switch; click a row to edit' },
    ],
  },
  {
    pageKey: 'events.playbook-editor',
    appKey: 'events',
    title: 'Playbook',
    route: '/app/events/playbooks/new',
    menuPath: 'Events › Automation › Playbooks › New playbook (or click a playbook)',
    accessNote: 'Event.Playbook.Manage right (Admin and EventsAdmin have it)',
    purpose: 'Name a playbook and list its items: tasks, projects, meetings, bookings, budgets, purchase requests and announcements, timed from the event.',
    sortOrder: 130,
    tabs: [],
    anchors: [
      ...common('events.playbook-editor'),
      { id: 'events.playbook-editor.save', what: 'Create playbook or Save button' },
      { id: 'events.playbook-editor.items', what: 'Items card: what the playbook creates, in plan order' },
      { id: 'events.playbook-editor.add-item', what: 'Add item button' },
      { id: 'events.playbook-editor.details', what: 'Playbook card: name, description and Active' },
      { id: 'events.playbook-editor.how-it-works', what: 'How it works card' },
    ],
  },
  {
    pageKey: 'events.types',
    appKey: 'events',
    title: 'Event types',
    route: '/app/events/types',
    menuPath: 'Events › Settings › Event types',
    accessNote: 'Event.Type.View to view (Events Access has it); Event.Type.Create, Event.Type.Update and Event.Type.Delete to change',
    purpose: 'Categories for events with their colour, default start time, campus service times and whether they show in Carpool and the shop. Planning Center tags map to these.',
    sortOrder: 140,
    tabs: [],
    anchors: [
      ...common('events.types'),
      { id: 'events.types.new-type', what: 'New type button' },
      { id: 'events.types.table', what: 'Event types table; click a row to edit' },
    ],
  },
];

// ====================================================================== Carpool (/app/rides)

const RIDES_ADMIN = 'Carpool admins: Admin or RidesAdmin role, a Rides manage right or Rides.Read (security-only users go to the security desk)';
const RIDES_VERIFY = 'Admin, RidesAdmin or RidesSecurity role, or the Rides.Verification.Read right';
const RIDES_SETTINGS = 'Admin or RidesAdmin role, or the Rides.Settings.Manage right';

const RIDES_PAGES: HelpPageDef[] = [
  {
    pageKey: 'rides.dashboard',
    appKey: 'rides',
    title: 'Carpool dashboard',
    route: '/app/rides',
    menuPath: 'Carpool › Overview › Dashboard',
    accessNote: RIDES_ADMIN,
    purpose: 'Verified church carpooling at a glance: drivers, riders, pending checks, open listings, bookings, disputes, 30 days of activity and top routes.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('rides.dashboard'),
      { id: 'rides.dashboard.stats', what: 'The six stat tiles' },
      { id: 'rides.dashboard.stat-drivers', what: 'Drivers tile: active and fully verified drivers; opens Drivers' },
      { id: 'rides.dashboard.stat-pending', what: 'Pending checks tile: documents waiting for a physical check; opens the verification queue' },
      { id: 'rides.dashboard.stat-disputes', what: 'Open disputes tile; opens Disputes' },
      { id: 'rides.dashboard.activity', what: 'Activity chart: listings, bookings and cancellations over 30 days' },
      { id: 'rides.dashboard.attention', what: 'Needs attention card: things worth a look today, each one clickable' },
      { id: 'rides.dashboard.top-routes', what: 'Top routes table: where drivers are offering lifts right now' },
    ],
  },
  {
    pageKey: 'rides.drivers',
    appKey: 'rides',
    title: 'Drivers',
    route: '/app/rides/drivers',
    menuPath: 'Carpool › Operations › Drivers',
    accessNote: RIDES_ADMIN,
    purpose: 'Everyone who registered a vehicle in the Carpool portal, with verification state and activity. Open a driver to verify, review documents or suspend.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...common('rides.drivers'),
      { id: 'rides.drivers.quick-filters', what: 'All, Verified, Pending and Suspended tiles (click to filter)' },
      { id: 'rides.drivers.filters', what: 'Search by name, email or number plate, campus and Clear' },
      { id: 'rides.drivers.table', what: 'Drivers table; click a row to open the driver' },
    ],
  },
  {
    pageKey: 'rides.driver-detail',
    appKey: 'rides',
    title: 'Driver',
    route: '/app/rides/drivers/:id',
    menuPath: 'Carpool › Operations › Drivers, then open a driver',
    accessNote: `${RIDES_ADMIN}, or verifiers (${RIDES_VERIFY}). Suspend needs Rides.Drivers.Manage; decisions need Rides.Verification.Record`,
    purpose: 'One driver: profile, vehicle and verification state. Whether they may list rides. Their checks, documents, listings and bookings.',
    sortOrder: 30,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Listing readiness, activity tiles, profile, vehicle and licence. Default tab.' },
      { key: 'verification', title: 'Verification', purpose: 'The government ID and driver licence checks: Mark verified, Mark failed or Revoke. ?tab=verification.' },
      { key: 'documents', title: 'Documents', purpose: 'Copies the driver uploaded (licence photo and others): view, accept or reject. Only for verifiers. ?tab=documents.' },
      { key: 'listings', title: 'Listings', purpose: 'Rides this driver offered; cancel a live listing. ?tab=listings.' },
      { key: 'bookings', title: 'Bookings', purpose: 'Riders who booked a seat with this driver. ?tab=bookings.' },
    ],
    anchors: [
      ...common('rides.driver-detail', { tabs: ['overview', 'verification', 'documents', 'listings', 'bookings'] }),
      { id: 'rides.driver-detail.suspend', what: 'Suspend button (Rides.Drivers.Manage); Unsuspend shows in its place for a suspended driver' },
      { id: 'rides.driver-detail.profile', what: 'Driver card: photo, name, verification badge, contact details' },
      { id: 'rides.driver-detail.checks', what: 'Vehicle, seats, ID and licence check badges and missing documents' },
      { id: 'rides.driver-detail.tabs', what: 'The tab buttons' },
      { id: 'rides.driver-detail.tab-overview', what: 'Overview tab button' },
      { id: 'rides.driver-detail.tab-verification', what: 'Verification tab button (count = checks waiting)' },
      { id: 'rides.driver-detail.tab-documents', what: 'Documents tab button (count = uploads waiting; verifiers only)' },
      { id: 'rides.driver-detail.tab-listings', what: 'Listings tab button' },
      { id: 'rides.driver-detail.tab-bookings', what: 'Bookings tab button' },
      { id: 'rides.driver-detail.overview-stats', what: 'Open listings, listings, bookings carried and registered tiles', tab: 'overview' },
      { id: 'rides.driver-detail.readiness', what: 'Listing readiness card: everything checked before the driver can list', tab: 'overview' },
      { id: 'rides.driver-detail.vehicle', what: 'Vehicle card: number plate, make, model and seats', tab: 'overview' },
      { id: 'rides.driver-detail.verification-cards', what: 'Government ID and driver licence check cards', tab: 'verification' },
      { id: 'rides.driver-detail.documents', what: 'Uploaded documents with what is still needed', tab: 'documents' },
      { id: 'rides.driver-detail.listings', what: 'Listings table', tab: 'listings' },
      { id: 'rides.driver-detail.bookings', what: 'Bookings table', tab: 'bookings' },
    ],
  },
  {
    pageKey: 'rides.verifications',
    appKey: 'rides',
    title: 'Verification queue',
    route: '/app/rides/verifications',
    menuPath: 'Carpool › Operations › Verification queue',
    accessNote: `${RIDES_VERIFY}. Recording a decision needs Rides.Verification.Record (RidesSecurity has it)`,
    purpose: 'Document checks waiting for a physical inspection, oldest first. Compare the document with the person and record the outcome on each card.',
    sortOrder: 40,
    tabs: [],
    anchors: [
      ...common('rides.verifications'),
      { id: 'rides.verifications.stats', what: 'Waiting, longest wait and verified tiles' },
      { id: 'rides.verifications.filters', what: 'Search, document type and status filters' },
      { id: 'rides.verifications.queue', what: 'The check cards with Mark verified, Mark failed and View for uploaded copies' },
    ],
  },
  {
    pageKey: 'rides.documents',
    appKey: 'rides',
    title: 'Documents',
    route: '/app/rides/documents',
    menuPath: 'Carpool › Operations › Documents',
    accessNote: RIDES_VERIFY,
    purpose: 'Copies of licences, IDs and other documents members uploaded. Review them, accept or reject them and see who opened what. Every view is logged.',
    sortOrder: 50,
    tabs: [],
    anchors: [
      ...common('rides.documents'),
      { id: 'rides.documents.settings-link', what: 'Document settings button (Rides.Settings.Manage)' },
      { id: 'rides.documents.status-tiles', what: 'Waiting for review, accepted, rejected and expired tiles (click to filter)' },
      { id: 'rides.documents.filters', what: 'Search and filters' },
      { id: 'rides.documents.table', what: 'Documents table with view, accept, reject and the view log' },
    ],
  },
  {
    pageKey: 'rides.bookings',
    appKey: 'rides',
    title: 'Bookings',
    route: '/app/rides/bookings',
    menuPath: 'Carpool › Operations › Bookings',
    accessNote: `${RIDES_ADMIN}. Cancelling needs Rides.Bookings.Manage`,
    purpose: 'Seats riders booked on drivers\' listings. Filter by status, event, campus and date, open a booking or cancel it.',
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...common('rides.bookings'),
      { id: 'rides.bookings.filters', what: 'Search, status, event, campus, date range, Include past and Clear' },
      { id: 'rides.bookings.table', what: 'Bookings table; click a row to open the booking' },
    ],
  },
  {
    pageKey: 'rides.listings',
    appKey: 'rides',
    title: 'Ride listings',
    route: '/app/rides/listings',
    menuPath: 'Carpool › Operations › Listings',
    accessNote: `${RIDES_ADMIN}. Cancelling needs Rides.Bookings.Manage`,
    purpose: 'Lifts drivers are offering for upcoming events. Filter them, open the driver or cancel a live listing.',
    sortOrder: 70,
    tabs: [],
    anchors: [
      ...common('rides.listings'),
      { id: 'rides.listings.filters', what: 'Search, status, event, campus, date range, Include past and Clear' },
      { id: 'rides.listings.table', what: 'Listings table with Open driver and Cancel listing' },
    ],
  },
  {
    pageKey: 'rides.disputes',
    appKey: 'rides',
    title: 'Disputes',
    route: '/app/rides/disputes',
    menuPath: 'Carpool › Operations › Disputes',
    accessNote: `${RIDES_ADMIN}. Deciding needs Rides.Disputes.Manage`,
    purpose: 'Complaints a rider or driver raised on a booking. Open one, read it, set the status and write resolution notes for the people involved.',
    sortOrder: 80,
    tabs: [],
    anchors: [
      ...common('rides.disputes'),
      { id: 'rides.disputes.status-tiles', what: 'Open and under review tiles (click to filter)' },
      { id: 'rides.disputes.filters', what: 'Status filter' },
      { id: 'rides.disputes.table', what: 'Disputes table; click a row to open and decide' },
    ],
  },
  {
    pageKey: 'rides.security',
    appKey: 'rides',
    title: 'Security desk',
    route: '/app/rides/security',
    menuPath: 'Carpool › Security › Expected drivers',
    accessNote: 'Admin, RidesAdmin or RidesSecurity role, or the Rides.Security.Check right',
    purpose: 'Drivers expected at a campus on a date: check the number plate and the driver against this list and record the ID checked mark at pickup or drop-off.',
    sortOrder: 90,
    tabs: [],
    anchors: [
      ...common('rides.security'),
      { id: 'rides.security.pickers', what: 'Campus, date and service time pickers (and the pickup point)' },
      { id: 'rides.security.stats', what: 'Drivers expected, riders booked, arrived and ID checked tiles' },
      { id: 'rides.security.table', what: 'Expected drivers with plate, vehicle, checks, riders, documents and the ID checked mark' },
    ],
  },
  {
    pageKey: 'rides.campuses',
    appKey: 'rides',
    title: 'Campuses',
    route: '/app/rides/campuses',
    menuPath: 'Carpool › Configuration › Campuses',
    accessNote: `${RIDES_ADMIN}. Changing campuses and rules needs Rides.Campuses.Manage`,
    purpose: 'The church locations riders and drivers can pick for pick-up and drop-off. They come from Locations; set the campus rules and pickup instructions here.',
    sortOrder: 100,
    tabs: [],
    anchors: [
      ...common('rides.campuses'),
      { id: 'rides.campuses.locations-link', what: 'Edit addresses and service times in Locations button' },
      { id: 'rides.campuses.sync', what: 'Sync from Locations button' },
      { id: 'rides.campuses.rules', what: 'Campus rules: every location is a campus, only with an address, countries' },
      { id: 'rides.campuses.list', what: 'The campuses with their badges, pickup instructions and edit' },
    ],
  },
  {
    pageKey: 'rides.events',
    appKey: 'rides',
    title: 'Carpool events',
    route: '/app/rides/events',
    menuPath: 'Carpool › Configuration › Events',
    accessNote: `${RIDES_ADMIN}. Show in Carpool needs Rides.Events.Manage`,
    purpose: 'Which calendar events drivers can offer lifts to and riders can book for. Events themselves are made in the Events app.',
    sortOrder: 110,
    tabs: [],
    anchors: [
      ...common('rides.events'),
      { id: 'rides.events.manage-link', what: 'Manage events button (opens the Events app)' },
      { id: 'rides.events.range', what: 'Month or 3 months switch' },
      { id: 'rides.events.navigator', what: 'Previous, next and Today, with how many are shown in Carpool' },
      { id: 'rides.events.visibility-filter', what: 'All, Shown and Hidden filter' },
      { id: 'rides.events.month', what: 'The first month of events with the Show in Carpool switches' },
    ],
  },
  {
    pageKey: 'rides.settings',
    appKey: 'rides',
    title: 'Carpool settings',
    route: '/app/rides/admin/settings',
    menuPath: 'Carpool › Administration › Settings',
    accessNote: RIDES_SETTINGS,
    purpose: 'Every rule, limit, switch and legal text of the Carpool service. Changes apply straight away, with one save bar for all tabs.',
    sortOrder: 120,
    tabs: [
      { key: 'safety', title: 'Safety', purpose: 'Matching rules, age thresholds, profile photos, safeguarding and driver standards. Default tab.' },
      { key: 'verification', title: 'Verification', purpose: 'The verification provider, what must be verified and which documents are accepted. ?tab=verification.' },
      { key: 'documents', title: 'Documents', purpose: 'Document uploads, retention (POPIA), who can open documents and the document types. ?tab=documents.' },
      { key: 'listings', title: 'Listings', purpose: 'Vehicle and seat limits and when listings may be created. ?tab=listings.' },
      { key: 'bookings', title: 'Bookings', purpose: 'Booking limits and the ride day rules (driver arrived). ?tab=bookings.' },
      { key: 'notifications', title: 'Notifications', purpose: 'The notification master switch, channels and individual topics. ?tab=notifications.' },
      { key: 'portal', title: 'Portal', purpose: 'Maintenance mode, feature switches, beta rollout, contact details and the portal address. ?tab=portal.' },
      { key: 'legal', title: 'Legal', purpose: 'Consent versions, the privacy notice and the terms of use. ?tab=legal.' },
      { key: 'system', title: 'System', purpose: 'Background sweeps and cache lifetimes. ?tab=system.' },
    ],
    anchors: [
      ...common('rides.settings', { tabs: ['safety', 'verification', 'documents', 'listings', 'bookings', 'notifications', 'portal', 'legal', 'system'] }),
      { id: 'rides.settings.copy-link', what: 'Edit portal copy and wording link' },
      { id: 'rides.settings.tabs', what: 'The settings tab buttons (a dot means unsaved changes)' },
      { id: 'rides.settings.tab-safety', what: 'Safety tab button' },
      { id: 'rides.settings.tab-verification', what: 'Verification tab button' },
      { id: 'rides.settings.tab-documents', what: 'Documents tab button' },
      { id: 'rides.settings.tab-listings', what: 'Listings tab button' },
      { id: 'rides.settings.tab-bookings', what: 'Bookings tab button' },
      { id: 'rides.settings.tab-notifications', what: 'Notifications tab button' },
      { id: 'rides.settings.tab-portal', what: 'Portal tab button' },
      { id: 'rides.settings.tab-legal', what: 'Legal tab button' },
      { id: 'rides.settings.tab-system', what: 'System tab button' },
      { id: 'rides.settings.matching-rules', what: 'Matching rules card', tab: 'safety' },
      { id: 'rides.settings.provider', what: 'Verification provider card', tab: 'verification' },
      { id: 'rides.settings.document-uploads', what: 'Document uploads card', tab: 'documents' },
      { id: 'rides.settings.document-types', what: 'Document types editor (saves per row)', tab: 'documents' },
      { id: 'rides.settings.vehicles', what: 'Vehicles and seats card', tab: 'listings' },
      { id: 'rides.settings.booking-limits', what: 'Booking limits card', tab: 'bookings' },
      { id: 'rides.settings.notification-channels', what: 'Notification master and channels card', tab: 'notifications' },
      { id: 'rides.settings.maintenance', what: 'Maintenance card (take the portal offline)', tab: 'portal' },
      { id: 'rides.settings.feature-switches', what: 'Portal feature switches card', tab: 'portal' },
      { id: 'rides.settings.consent-versions', what: 'Consent versions card', tab: 'legal' },
      { id: 'rides.settings.background-sweeps', what: 'Background sweeps card', tab: 'system' },
    ],
  },
  {
    pageKey: 'rides.copy',
    appKey: 'rides',
    title: 'Copy and wording',
    route: '/app/rides/admin/copy',
    menuPath: 'Carpool › Administration › Copy & wording',
    accessNote: RIDES_SETTINGS,
    purpose: 'Every text the Carpool portal and its notifications use. Each row saves on its own and is live straight away.',
    sortOrder: 130,
    tabs: [],
    anchors: [
      ...common('rides.copy'),
      { id: 'rides.copy.filters', what: 'Search and the All, Portal and Notifications filter' },
      { id: 'rides.copy.first-group', what: 'The first group of texts, each with its own save' },
    ],
  },
  {
    pageKey: 'rides.lookups',
    appKey: 'rides',
    title: 'Carpool lookups',
    route: '/app/rides/admin/lookups',
    menuPath: 'Carpool › Administration › Lookups',
    accessNote: RIDES_SETTINGS,
    purpose: 'The seeded status and type tables. Rename, recolour, describe or hide rows; rows cannot be added or deleted because the engine relies on their ids.',
    sortOrder: 140,
    tabs: [],
    anchors: [
      ...common('rides.lookups', { actions: false }),
      { id: 'rides.lookups.table-nav', what: 'The list of lookup tables' },
      { id: 'rides.lookups.table', what: 'The rows of the open table with edit' },
    ],
  },
];

// ====================================================================== HR (/app/members)

const HR_STAFF = 'Any signed-in staff member';
const HR_APPROVER = 'People set up as leave approvers, plus HR admins';
const HR_ADMIN = 'HR admins (HRAdmin role)';

/** The My Profile tabs (MemberProfile in self mode plus the two HR tabs). This page uses ?tb=, not ?tab=. */
const HR_PROFILE_TABS = ['personal', 'contact', 'addresses', 'health', 'family', 'campus', 'ministries', 'emergency', 'employment', 'files'];

const HR_PAGES: HelpPageDef[] = [
  // ---------------------------------------------------------------- Staff (My HR, My Records)
  {
    pageKey: 'hr.dashboard',
    appKey: 'hr',
    title: 'My HR dashboard',
    route: '/app/members/dashboard',
    menuPath: 'HR › My HR › Dashboard (also where /app/members lands)',
    accessNote: HR_STAFF,
    purpose: 'Your HR day at a glance: leave balance, pending requests, years at the church and your next important date. It also shows who in your team is away, policies waiting for your signature, birthdays, announcements and your latest leave requests.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('hr.dashboard'),
      { id: 'hr.dashboard.stats', what: 'Leave Balances, Pending Requests, Years at Company and Important Dates tiles; each opens My Leaves or My Profile' },
      { id: 'hr.dashboard.team-leave', what: 'Team Leave card: colleagues on leave now and those starting soon' },
      { id: 'hr.dashboard.policies', what: 'Policies Awaiting Signature card: pending and overdue policies; click one to read and sign it' },
      { id: 'hr.dashboard.birthdays', what: 'Birthdays card: team birthdays today and coming up' },
      { id: 'hr.dashboard.announcements', what: 'Announcements card: the latest HR announcements with View all' },
      { id: 'hr.dashboard.recent-leaves', what: 'My Recent Leave Requests card: your last five requests with Apply and View all' },
      { id: 'hr.dashboard.quick-actions', what: 'Quick Actions card: Apply for Leave, My Leaves, My Profile, My Files, My Policies and Announcements' },
      { id: 'hr.dashboard.holidays', what: 'Upcoming Public Holidays card' },
    ],
  },
  {
    pageKey: 'hr.calendar',
    appKey: 'hr',
    title: 'Calendar',
    route: '/app/members/calendar',
    menuPath: 'HR › My HR › Calendar',
    accessNote: HR_STAFF,
    purpose: 'A month calendar of your own leave, public holidays and team birthdays. Switch each one on or off, click a leave to open it or a birthday to see the colleague. Subscribe gives you a private live feed for Google, Apple or Outlook calendars.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...common('hr.calendar', { actions: false }),
      { id: 'hr.calendar.toolbar', what: 'Filter card: the My leaves, Public holidays and Birthdays chips, the colour legend and Subscribe' },
      { id: 'hr.calendar.streams', what: 'My leaves, Public holidays and Birthdays chips (switch each on or off)' },
      { id: 'hr.calendar.subscribe', what: 'Subscribe button: copy your private calendar feed link or generate a new one' },
      { id: 'hr.calendar.calendar', what: 'The month calendar; click a leave to open it or a birthday to see the colleague' },
    ],
  },
  {
    pageKey: 'hr.announcements',
    appKey: 'hr',
    title: 'Announcements',
    route: '/app/members/announcements',
    menuPath: 'HR › My HR › Announcements',
    accessNote: HR_STAFF,
    purpose: 'Every active HR announcement in full, with its attachments. Filter by type or search the title and text. Drafts and archived announcements never show here.',
    sortOrder: 30,
    tabs: [],
    anchors: [
      ...common('hr.announcements'),
      { id: 'hr.announcements.toolbar', what: 'Filter card: type chips and search' },
      { id: 'hr.announcements.type-filter', what: 'All and announcement type chips' },
      { id: 'hr.announcements.search', what: 'Search title or body box' },
      { id: 'hr.announcements.feed', what: 'The announcements, newest first, with full text and attachments to open' },
    ],
  },
  {
    pageKey: 'hr.approvals',
    appKey: 'hr',
    title: 'Leave Approvals',
    route: '/app/members/approvals',
    menuPath: 'HR › My HR › Leave Approvals (shows for line approvers; the approval email and push open /app/members/approvals/:id with that leave already open)',
    accessNote: `${HR_APPROVER}. It follows the data, not a role: you see leave where you are an active line approver, never your own`,
    purpose: 'The line approver inbox: leave waiting for your decision, leave you sent back to the employee and your past decisions. Open a row to read the leave, check duty cover, balances and colleagues away, then approve, reject, ask for duty cover or ask a question. Leave you approve then goes to HR for final approval.',
    sortOrder: 40,
    tabs: [
      { key: 'pending', title: 'Pending', purpose: 'Line steps waiting for your decision; open a row to decide. Default tab.' },
      { key: 'waiting', title: 'Waiting on employee', purpose: 'Leave you sent back for duty cover or a question; it stays here until the employee replies. ?tab=waiting.' },
      { key: 'history', title: 'History', purpose: 'Leave you approved, rejected or sent back, latest first, with where each one is now. ?tab=history.' },
    ],
    anchors: [
      ...common('hr.approvals', { tabs: ['pending', 'waiting', 'history'] }),
      { id: 'hr.approvals.tabs', what: 'The tab buttons (the counts show what is waiting)' },
      { id: 'hr.approvals.tab-pending', what: 'Pending tab button (count = leave waiting for you)' },
      { id: 'hr.approvals.tab-waiting', what: 'Waiting on employee tab button (count = leave you sent back)' },
      { id: 'hr.approvals.tab-history', what: 'History tab button' },
      { id: 'hr.approvals.list-pending', what: 'Pending list: employee, leave type, dates, days, department, duty cover and applied on; click a row to open the decision drawer', tab: 'pending' },
      { id: 'hr.approvals.list-waiting', what: 'Waiting on employee list with what you asked and when', tab: 'waiting' },
      { id: 'hr.approvals.list-history', what: 'History list with where each leave is now (paged)', tab: 'history' },
      { id: 'hr.approvals.drawer-leave', what: 'Decision drawer, The leave card: dates, days, leave type, reason and the employee comment (open a row first)' },
      { id: 'hr.approvals.drawer-decision', what: 'Decision drawer, Your decision card: the comment box and the action buttons (only when you can act)' },
      { id: 'hr.approvals.drawer-ask', what: 'Decision drawer: Ask for duty cover and Ask a question (only before a request is open)' },
      { id: 'hr.approvals.drawer-decide', what: 'Decision drawer: Reject (needs a comment) and Approve' },
      { id: 'hr.approvals.drawer-waiting', what: 'Decision drawer: the open request to the employee with Withdraw request (only while one is open)' },
      { id: 'hr.approvals.drawer-steps', what: 'Decision drawer, Approval steps card: the chain timeline with requests to the employee and cover added' },
      { id: 'hr.approvals.drawer-context', what: 'Decision drawer: duty cover, the employee departments, leave balances and colleagues away at the same time' },
    ],
  },
  {
    pageKey: 'hr.my-profile',
    appKey: 'hr',
    title: 'My Profile',
    route: '/app/members/my-profile',
    menuPath: 'HR › My Records › My Profile',
    accessNote: `${HR_STAFF}. Employment and Files show only when you have a staff record`,
    purpose: 'Your own member record (personal details, contact, addresses, health, family, campus, ministries and emergency contacts) and, with a staff record, your employment details and HR files. The meter shows how complete your profile is. This page opens tabs with ?tb= (not ?tab=), so tour steps use routes like "?tb=health".',
    sortOrder: 50,
    tabs: [
      { key: 'personal', title: 'Personal', purpose: 'Names, date of birth, gender, marital status and the hide my birthday switch. Default tab (no ?tb=).' },
      { key: 'contact', title: 'Contact', purpose: 'Cellphone, telephone and an alternative contact; the email belongs to your login and cannot change here. ?tb=contact.' },
      { key: 'addresses', title: 'Addresses', purpose: 'Add, edit and remove your addresses. ?tb=addresses.' },
      { key: 'health', title: 'Health', purpose: 'Allergies and disabilities, each with an optional note. ?tb=health.' },
      { key: 'family', title: 'Family', purpose: 'The households you belong to and the people in them (read only here). ?tb=family.' },
      { key: 'campus', title: 'Campus', purpose: 'Your home campus and the campus you serve at. ?tb=campus.' },
      { key: 'ministries', title: 'Ministries and roles', purpose: 'Ministries, departments and designations you belong to (read only; changed in Departments). ?tb=ministries.' },
      { key: 'emergency', title: 'Emergency contacts', purpose: 'Who to call in an emergency, with one primary contact. ?tb=emergency.' },
      { key: 'employment', title: 'Employment', purpose: 'Your staff record: employee number, job title, type, status and tenure; you can edit the number and job title. Only with a staff record. ?tb=employment.' },
      { key: 'files', title: 'Files', purpose: 'Your personal HR files: view, add and edit them (HR only files are hidden). Only with a staff record. ?tb=files.' },
    ],
    anchors: [
      ...common('hr.my-profile', { actions: false, tabs: HR_PROFILE_TABS }),
      { id: 'hr.my-profile.header', what: 'Profile header: photo, name, member code, type and status badges plus staff number, employment, tenure and PaySpace badges' },
      { id: 'hr.my-profile.completeness', what: 'Profile complete meter with what is still missing' },
      { id: 'hr.my-profile.tabs', what: 'The tab buttons (scroll sideways on small screens)' },
      { id: 'hr.my-profile.tab-personal', what: 'Personal tab button' },
      { id: 'hr.my-profile.tab-contact', what: 'Contact tab button' },
      { id: 'hr.my-profile.tab-addresses', what: 'Addresses tab button' },
      { id: 'hr.my-profile.tab-health', what: 'Health tab button' },
      { id: 'hr.my-profile.tab-family', what: 'Family tab button' },
      { id: 'hr.my-profile.tab-campus', what: 'Campus tab button' },
      { id: 'hr.my-profile.tab-ministries', what: 'Ministries and roles tab button' },
      { id: 'hr.my-profile.tab-emergency', what: 'Emergency contacts tab button' },
      { id: 'hr.my-profile.tab-employment', what: 'Employment tab button (only with a staff record)' },
      { id: 'hr.my-profile.tab-files', what: 'Files tab button (only with a staff record)' },
      { id: 'hr.my-profile.edit-employment', what: 'Edit employment details button (employee number and job title)', tab: 'employment' },
      { id: 'hr.my-profile.employment', what: 'Employment record and Tenure cards', tab: 'employment' },
      { id: 'hr.my-profile.files-toolbar', what: 'File count, Table or Grid switch and Add File', tab: 'files' },
      { id: 'hr.my-profile.add-file', what: 'Add File button', tab: 'files' },
      { id: 'hr.my-profile.files-list', what: 'Your files; click one to preview it', tab: 'files' },
    ],
  },
  {
    pageKey: 'hr.my-leaves',
    appKey: 'hr',
    title: 'My Leave Requests',
    route: '/app/members/my-leaves',
    menuPath: 'HR › My Records › My Leaves',
    accessNote: HR_STAFF,
    purpose: 'Every leave request you have made, latest first, with where each one is in the approval chain. Apply for new leave from here. Leave tagged Action needed is waiting on you because your approver asked for duty cover or a question.',
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...common('hr.my-leaves'),
      { id: 'hr.my-leaves.new-leave', what: 'New Leave Request button (opens the leave wizard)' },
      { id: 'hr.my-leaves.stats', what: 'Total, Pending, Approved and Rejected tiles (click to filter)' },
      { id: 'hr.my-leaves.toolbar', what: 'Search, the number shown and the Table, Item or Grid switch' },
      { id: 'hr.my-leaves.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.my-leaves.action-needed', what: 'Your approver needs something from you banner (only when a leave waits on you)' },
      { id: 'hr.my-leaves.list', what: 'Your leave requests in the chosen view; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.my-leaves-new',
    appKey: 'hr',
    title: 'New Leave Request',
    route: '/app/members/my-leaves/new',
    menuPath: 'HR › Quick Actions › Apply for Leave (or New Leave Request on My Leaves)',
    accessNote: HR_STAFF,
    purpose: 'A six step wizard to apply for leave: Important Info, Basic Details (type and dates), Additional (reason and comment), Duties (who covers your work), Documents and Review. Steps open one after the other, so a tour explains them from the stepper. Submitting sends the leave to your line approver, then HR.',
    sortOrder: 70,
    tabs: [],
    anchors: [
      ...common('hr.my-leaves-new'),
      { id: 'hr.my-leaves-new.start-fresh', what: 'Start Fresh button: clears every step' },
      { id: 'hr.my-leaves-new.stepper', what: 'The six steps: Important Info, Basic Details, Additional, Duties, Documents and Review (click a finished step to go back)' },
      { id: 'hr.my-leaves-new.step-content', what: 'The open step' },
      { id: 'hr.my-leaves-new.facts', what: 'Important Info step: Pending Tasks, Leave Balance and Leave Policy cards (first step only)' },
      { id: 'hr.my-leaves-new.acknowledge', what: 'Important Info step: the I have read and understood tick box needed to go on (first step only)' },
      { id: 'hr.my-leaves-new.nav', what: 'Cancel, Back and Next buttons (Next reads Review on step five and becomes Submit Leave on the last step)' },
      { id: 'hr.my-leaves-new.next', what: 'Next button (Review on step five); it checks the step first' },
      { id: 'hr.my-leaves-new.submit', what: 'Submit Leave button (Review step only)' },
    ],
  },
  {
    pageKey: 'hr.my-leave',
    appKey: 'hr',
    title: 'My leave',
    route: '/app/members/my-leaves/:id',
    menuPath: 'HR › My Records › My Leaves, then open a leave',
    accessNote: `${HR_STAFF} (your own leave only). Edit is for pending leave; cancel is possible until the leave starts`,
    purpose: 'One of your leave requests: its dates, decision and where it is in the approval chain. When your approver asks for duty cover or a question, answer it here and send it back. Add duty delegations and supporting documents in the tabs.',
    sortOrder: 80,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Reason for leave, your comment and the HR note. Default tab.' },
      { key: 'duties', title: 'Duties', purpose: 'Who covers your duties while you are away; add or remove delegations. ?tab=duties.' },
      { key: 'documents', title: 'Documents', purpose: 'Supporting documents such as a medical certificate; add or open them. ?tab=documents.' },
      { key: 'audit', title: 'Audit', purpose: 'When you applied, who decided and when. ?tab=audit.' },
    ],
    anchors: [
      ...common('hr.my-leave', { tabs: ['overview', 'duties', 'documents', 'audit'] }),
      { id: 'hr.my-leave.edit', what: 'Edit button (pending leave only)' },
      { id: 'hr.my-leave.cancel', what: 'Cancel Request button (until the leave starts)' },
      { id: 'hr.my-leave.info-cards', what: 'Period, Decision and Employee cards' },
      { id: 'hr.my-leave.ask-banner', what: 'Your approver asked you banner: add duty cover or answer the question, then send it back (only while a request is open)' },
      { id: 'hr.my-leave.approval-progress', what: 'Approval progress card: the stage and the chain timeline from line approval to HR' },
      { id: 'hr.my-leave.tabs', what: 'The tab buttons' },
      { id: 'hr.my-leave.tab-overview', what: 'Overview tab button' },
      { id: 'hr.my-leave.tab-duties', what: 'Duties tab button (count = delegations)' },
      { id: 'hr.my-leave.tab-documents', what: 'Documents tab button (count = documents)' },
      { id: 'hr.my-leave.tab-audit', what: 'Audit tab button' },
      { id: 'hr.my-leave.reason', what: 'Reason for leave and My comment cards', tab: 'overview' },
      { id: 'hr.my-leave.duties', what: 'Duty delegations list', tab: 'duties' },
      { id: 'hr.my-leave.add-delegation', what: 'Add delegation button', tab: 'duties' },
      { id: 'hr.my-leave.documents', what: 'Documents list with Add document', tab: 'documents' },
      { id: 'hr.my-leave.audit', what: 'Application and Decision cards', tab: 'audit' },
    ],
  },
  {
    pageKey: 'hr.my-leave-edit',
    appKey: 'hr',
    title: 'Edit Leave Request',
    route: '/app/members/my-leaves/:id/edit',
    menuPath: 'HR › My Records › My Leaves, open a pending leave, then Edit',
    accessNote: `${HR_STAFF} (your own pending leave only)`,
    purpose: 'Change the type, dates, reason or comment of a leave request that is still pending. The number of days follows the dates. Leave that is decided can no longer be edited.',
    sortOrder: 90,
    tabs: [],
    anchors: [
      ...common('hr.my-leave-edit', { actions: false }),
      { id: 'hr.my-leave-edit.form', what: 'The edit form' },
      { id: 'hr.my-leave-edit.type', what: 'Leave Type picker' },
      { id: 'hr.my-leave-edit.dates', what: 'Start Date and End Date' },
      { id: 'hr.my-leave-edit.days', what: 'Number of Days (worked out from the dates)' },
      { id: 'hr.my-leave-edit.save', what: 'Cancel and Save buttons' },
    ],
  },
  {
    pageKey: 'hr.my-duties',
    appKey: 'hr',
    title: 'My Duties',
    route: '/app/members/my-duties',
    menuPath: 'HR › My Records › My Duties',
    accessNote: HR_STAFF,
    purpose: 'The roles and responsibilities HR assigned to you, with how often and how important each one is. Click a duty to read its description. Sunday and events duties are tagged.',
    sortOrder: 100,
    tabs: [],
    anchors: [
      ...common('hr.my-duties'),
      { id: 'hr.my-duties.toolbar', what: 'The number of duties and the Table, Item or Grid switch' },
      { id: 'hr.my-duties.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.my-duties.list', what: 'Your duties in the chosen view; click one for its details' },
    ],
  },
  {
    pageKey: 'hr.my-policies',
    appKey: 'hr',
    title: 'My Policies',
    route: '/app/members/my-policies',
    menuPath: 'HR › My Records › My Policies',
    accessNote: HR_STAFF,
    purpose: 'Every published church policy, with the ones assigned to you marked to sign, overdue or signed. Open a policy to read it, ask AI about it and sign it. Your signature history keeps a record of everything you signed.',
    sortOrder: 110,
    tabs: [],
    anchors: [
      ...common('hr.my-policies'),
      { id: 'hr.my-policies.history-link', what: 'Signature history button' },
      { id: 'hr.my-policies.stats', what: 'To sign, Overdue, Signed and All published tiles (click to filter)' },
      { id: 'hr.my-policies.toolbar', what: 'Search, the number shown and the Table, Item or Grid switch' },
      { id: 'hr.my-policies.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.my-policies.list', what: 'The policies in the chosen view; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.my-policy',
    appKey: 'hr',
    title: 'Policy',
    route: '/app/members/my-policies/:id',
    menuPath: 'HR › My Records › My Policies, then open a policy',
    accessNote: `${HR_STAFF}. Sign now shows only when the policy is assigned to you and still pending or overdue`,
    purpose: 'One policy: when it took effect, its document and your own status. Preview the document, ask AI questions about it and sign it when it is assigned to you. Policies not assigned to you can still be read.',
    sortOrder: 120,
    tabs: [],
    anchors: [
      ...common('hr.my-policy'),
      { id: 'hr.my-policy.sign', what: 'Sign now button (assigned and not yet signed)' },
      { id: 'hr.my-policy.badges', what: 'Policy badges: number, version, effective, signature required and your status' },
      { id: 'hr.my-policy.info-cards', what: 'Lifecycle, Document and My status cards' },
      { id: 'hr.my-policy.preview', what: 'Preview document button (when a document is attached)' },
      { id: 'hr.my-policy.my-status', what: 'My status card: your status, due date and signed date' },
      { id: 'hr.my-policy.ask-ai', what: 'Ask AI panel: questions answered from this policy document' },
    ],
  },
  {
    pageKey: 'hr.my-policies-history',
    appKey: 'hr',
    title: 'My Signature History',
    route: '/app/members/my-policies/history',
    menuPath: 'HR › My Records › My Policies, then Signature history',
    accessNote: HR_STAFF,
    purpose: 'A record of every policy you have signed: the policy, its version, when you signed and from which IP address. It is read only.',
    sortOrder: 130,
    tabs: [],
    anchors: [
      ...common('hr.my-policies-history', { actions: false }),
      { id: 'hr.my-policies-history.back', what: 'Back to my policies button' },
      { id: 'hr.my-policies-history.table', what: 'Signatures table: policy, version, signed and IP' },
    ],
  },
  {
    pageKey: 'hr.my-files',
    appKey: 'hr',
    title: 'My Files',
    route: '/app/members/my-files',
    menuPath: 'HR › My Records › My Files (the same files are on My Profile › Files)',
    accessNote: HR_STAFF,
    purpose: 'Your personal HR files, such as certificates and contracts. Add files, edit their details and expiry dates and preview them. Files HR marks as HR only, such as payroll documents, are not shown.',
    sortOrder: 140,
    tabs: [],
    anchors: [
      ...common('hr.my-files'),
      { id: 'hr.my-files.files-toolbar', what: 'File count, Table or Grid switch and Add File' },
      { id: 'hr.my-files.add-file', what: 'Add File button' },
      { id: 'hr.my-files.files-list', what: 'Your files in the chosen view; click one to preview it' },
    ],
  },

  // ---------------------------------------------------------------- HR Operations (HRAdmin)
  {
    pageKey: 'hr.admin-dashboard',
    appKey: 'hr',
    title: 'HR overview',
    route: '/app/members/admin/dashboard',
    menuPath: 'HR › HR Operations › Overview',
    accessNote: HR_ADMIN,
    purpose: 'Operational HR at a glance: headcount, who is on leave today, pending leave, unsigned policies, contracts ending and new hires. Below are charts, the pending leave and policy compliance lists, recent activity, birthdays, anniversaries, HR alerts and quick actions. One refresh updates every section.',
    sortOrder: 200,
    tabs: [],
    anchors: [
      ...common('hr.admin-dashboard'),
      { id: 'hr.admin-dashboard.kpis', what: 'The seven tiles: Total Employees, Active, On Leave Today, Pending Leave, Policies Awaiting, Contracts Expiring and New Hires; each opens the matching list' },
      { id: 'hr.admin-dashboard.charts', what: 'Employees by Type and Leave Applications (last 30 days) charts' },
      { id: 'hr.admin-dashboard.pending-leaves', what: 'Pending Leave Requests card' },
      { id: 'hr.admin-dashboard.policy-compliance', what: 'Policy Compliance card' },
      { id: 'hr.admin-dashboard.activity', what: 'Recent Activity feed' },
      { id: 'hr.admin-dashboard.contract-expiries', what: 'Contracts Expiring card' },
      { id: 'hr.admin-dashboard.alerts', what: 'HR alerts: leave pending over three days, expired contracts still active and policies unsigned over 30 days' },
      { id: 'hr.admin-dashboard.quick-actions', what: 'Quick Actions: Add Employee, Approve Leaves, Upload Policy, Compose Announcement and Manage PaySpace Sync' },
    ],
  },
  {
    pageKey: 'hr.employees',
    appKey: 'hr',
    title: 'Employees',
    route: '/app/members/admin/employees',
    menuPath: 'HR › HR Operations › Employees',
    accessNote: HR_ADMIN,
    purpose: 'Every staff record with type, status, tenure and PaySpace link. Filter, search and switch between table, item and grid views. Open an employee to manage their leave, files, duties and policies. New Employee adds a record.',
    sortOrder: 210,
    tabs: [],
    anchors: [
      ...common('hr.employees'),
      { id: 'hr.employees.new-employee', what: 'New Employee button' },
      { id: 'hr.employees.stats', what: 'Total, Active, Terminated and PaySpace Linked tiles (click to filter)' },
      { id: 'hr.employees.toolbar', what: 'Search, Filters, Clear All, page size, the number shown and the view switch' },
      { id: 'hr.employees.filters', what: 'Filters button (opens the filter drawer; the badge counts active filters)' },
      { id: 'hr.employees.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.employees.list', what: 'The employees in the chosen view; click one to open the record' },
    ],
  },
  {
    pageKey: 'hr.employee-create',
    appKey: 'hr',
    title: 'New employee record',
    route: '/app/members/admin/employees/create',
    menuPath: 'HR › HR Operations › Employees, then New Employee',
    accessNote: `${HR_ADMIN}. Only users with the Staff role can be picked`,
    purpose: 'Link an existing user to a new HR employment record. Pick the user, then add the employee number, job title, type, status and dates. Names, email and phone come from User Management and cannot be changed here.',
    sortOrder: 220,
    tabs: [],
    anchors: [
      ...common('hr.employee-create', { actions: false }),
      { id: 'hr.employee-create.staff-note', what: 'Note that only Staff role users without a record can be picked (before a user is picked)' },
      { id: 'hr.employee-create.selected-user', what: 'The picked user (after picking)' },
      { id: 'hr.employee-create.form', what: 'The employee form' },
      { id: 'hr.employee-create.user', what: 'User picker: type two or more letters to search' },
      { id: 'hr.employee-create.employment', what: 'Type and Status pickers' },
      { id: 'hr.employee-create.submit', what: 'Cancel and Create employee buttons' },
    ],
  },
  {
    pageKey: 'hr.employee',
    appKey: 'hr',
    title: 'Employee',
    route: '/app/members/admin/employees/:id',
    menuPath: 'HR › HR Operations › Employees, then open an employee (the id is the user id)',
    accessNote: HR_ADMIN,
    purpose: 'One staff member: contact details, employment and PaySpace link. The tabs hold their files, duties, leave, policies and their member, address and health details. HR can add files, assign duties, log leave and edit the employment record.',
    sortOrder: 230,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Identity and quick counts of files, duties, leave and policies. Default tab.' },
      { key: 'files', title: 'Files', purpose: 'The employee files (including HR only ones): preview, edit, delete and Add File. ?tab=files.' },
      { key: 'duties', title: 'Duties', purpose: 'Duties assigned to the employee and Assign Duty. ?tab=duties.' },
      { key: 'leaves', title: 'Leaves', purpose: 'Their leave requests, latest first. Log Leave records leave taken outside the portal. ?tab=leaves.' },
      { key: 'policies', title: 'Policies', purpose: 'Policies assigned to them and whether each is signed. ?tab=policies.' },
      { key: 'member', title: 'Member', purpose: 'Their church member record, editable by HR. ?tab=member.' },
      { key: 'addresses', title: 'Addresses', purpose: 'Their addresses, editable by HR. ?tab=addresses.' },
      { key: 'health', title: 'Health', purpose: 'Their allergies and disabilities, editable by HR. ?tab=health.' },
    ],
    anchors: [
      ...common('hr.employee', { tabs: ['overview', 'files', 'duties', 'leaves', 'policies', 'member', 'addresses', 'health'] }),
      { id: 'hr.employee.edit', what: 'Edit Employee button (employment record)' },
      { id: 'hr.employee.profile', what: 'Name, job title and badges: employee number, type, employment status, tenure and PaySpace' },
      { id: 'hr.employee.info-cards', what: 'Contact, Employment and PaySpace cards' },
      { id: 'hr.employee.tabs', what: 'The tab buttons (counts for files, duties, leave and policies)' },
      { id: 'hr.employee.tab-overview', what: 'Overview tab button' },
      { id: 'hr.employee.tab-files', what: 'Files tab button' },
      { id: 'hr.employee.tab-duties', what: 'Duties tab button' },
      { id: 'hr.employee.tab-leaves', what: 'Leaves tab button' },
      { id: 'hr.employee.tab-policies', what: 'Policies tab button' },
      { id: 'hr.employee.tab-member', what: 'Member tab button' },
      { id: 'hr.employee.tab-addresses', what: 'Addresses tab button' },
      { id: 'hr.employee.tab-health', what: 'Health tab button' },
      { id: 'hr.employee.overview', what: 'Identity and Quick Stats cards', tab: 'overview' },
      { id: 'hr.employee.add-file', what: 'Add File button', tab: 'files' },
      { id: 'hr.employee.assign-duty', what: 'Assign Duty button', tab: 'duties' },
      { id: 'hr.employee.log-leave', what: 'Log Leave button', tab: 'leaves' },
      { id: 'hr.employee.policies', what: 'Policy assignments in the chosen view', tab: 'policies' },
      { id: 'hr.employee.member', what: 'Member record with edit', tab: 'member' },
      { id: 'hr.employee.addresses', what: 'Address list with add and edit', tab: 'addresses' },
      { id: 'hr.employee.health', what: 'Allergies and disabilities', tab: 'health' },
    ],
  },
  {
    pageKey: 'hr.employee-files',
    appKey: 'hr',
    title: 'Employee Files',
    route: '/app/members/admin/employee-files',
    menuPath: 'Not in the menu: open /app/members/admin/employee-files (each employee also has a Files tab)',
    accessNote: HR_ADMIN,
    purpose: 'Every employee file across the organisation in one table, including HR only files. Shows who owns each file, its type, who can see it and when it expires. It is read only; manage files on the employee record.',
    sortOrder: 240,
    tabs: [],
    anchors: [
      ...common('hr.employee-files', { actions: false }),
      { id: 'hr.employee-files.table', what: 'Files table: file, owner, type, visibility (HR only or visible to staff), uploaded and expires' },
    ],
  },
  {
    pageKey: 'hr.leaves',
    appKey: 'hr',
    title: 'Leave Requests',
    route: '/app/members/admin/leaves',
    menuPath: 'HR › HR Operations › Leave Requests',
    accessNote: HR_ADMIN,
    purpose: 'Every leave request in the organisation, latest first. Filter by type, status and window, search and switch views. Open a leave to give final approval, reject, defer, cancel or adjust its dates.',
    sortOrder: 250,
    tabs: [],
    anchors: [
      ...common('hr.leaves'),
      { id: 'hr.leaves.stats', what: 'Total Applications, Pending Requests, On Leave Today and Upcoming tiles (click to filter)' },
      { id: 'hr.leaves.toolbar', what: 'Search, Filters, Clear All, page size, the number shown and the view switch' },
      { id: 'hr.leaves.filters', what: 'Filters button: type, status and window (on leave, upcoming or past)' },
      { id: 'hr.leaves.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.leaves.list', what: 'The leave requests in the chosen view; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.leave',
    appKey: 'hr',
    title: 'Leave request',
    route: '/app/members/admin/leaves/:id',
    menuPath: 'HR › HR Operations › Leave Requests, then open a leave',
    accessNote: `${HR_ADMIN}. Final approval waits for the line approver unless there is none; your own leave needs another HR admin unless you are the only one`,
    purpose: 'One leave request with everything HR needs to decide: the period, the approval chain, duty cover, this year totals and the live PaySpace balances. Give final approval, reject, defer, cancel, re-open or adjust the dates. Documents and the audit trail are in the tabs.',
    sortOrder: 260,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Reason, employee comment, HR note and the decision panel while pending. Default tab.' },
      { key: 'documents', title: 'Documents', purpose: 'Supporting documents from the employee or HR; preview, open or add. ?tab=documents.' },
      { key: 'audit', title: 'Audit', purpose: 'Created, modified, applied and who decided when. ?tab=audit.' },
    ],
    anchors: [
      ...common('hr.leave', { tabs: ['overview', 'documents', 'audit'] }),
      { id: 'hr.leave.adjust-dates', what: 'Adjust dates button, for example when the employee came back early (not for rejected or cancelled leave)' },
      { id: 'hr.leave.reject', what: 'Reject button (pending leave)' },
      { id: 'hr.leave.approve', what: 'Give final approval button (pending leave; the hover text says why when it is blocked)' },
      { id: 'hr.leave.title', what: 'Employee name, leave type and status badges' },
      { id: 'hr.leave.info-cards', what: 'Period, Decision and Employee cards' },
      { id: 'hr.leave.approval-chain', what: 'Approval chain card: stage, timeline, duty cover and Open in Final Approvals' },
      { id: 'hr.leave.context', what: 'Leave context and PaySpace leave balances cards' },
      { id: 'hr.leave.tabs', what: 'The tab buttons' },
      { id: 'hr.leave.tab-overview', what: 'Overview tab button' },
      { id: 'hr.leave.tab-documents', what: 'Documents tab button (count = documents)' },
      { id: 'hr.leave.tab-audit', what: 'Audit tab button' },
      { id: 'hr.leave.reason', what: 'Reason for leave and Employee comment cards', tab: 'overview' },
      { id: 'hr.leave.decision', what: 'Decision card: HR note, Reject and Give final approval (pending leave only)', tab: 'overview' },
      { id: 'hr.leave.documents', what: 'Documents list with Add Document', tab: 'documents' },
      { id: 'hr.leave.audit', what: 'Application and Decision cards', tab: 'audit' },
    ],
  },
  {
    pageKey: 'hr.policies',
    appKey: 'hr',
    title: 'Policies',
    route: '/app/members/admin/policies',
    menuPath: 'HR › HR Operations › Policies',
    accessNote: HR_ADMIN,
    purpose: 'Every HR policy with its category, version, effective date and whether it needs a signature. Filter, search and switch views. Open a policy to publish it to its signatories and follow who has signed.',
    sortOrder: 270,
    tabs: [],
    anchors: [
      ...common('hr.policies'),
      { id: 'hr.policies.new-policy', what: 'New Policy button' },
      { id: 'hr.policies.stats', what: 'Total, Currently effective, Pending future date and Signature required tiles (click to filter)' },
      { id: 'hr.policies.toolbar', what: 'Search, Filters, Clear All, page size, the number shown and the view switch' },
      { id: 'hr.policies.filters', what: 'Filters button (opens the filter drawer)' },
      { id: 'hr.policies.view-switch', what: 'Table, Item or Grid switch' },
      { id: 'hr.policies.list', what: 'The policies in the chosen view; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.policy-create',
    appKey: 'hr',
    title: 'New Policy',
    route: '/app/members/admin/policies/create',
    menuPath: 'HR › HR Operations › Policies, then New Policy',
    accessNote: HR_ADMIN,
    purpose: 'Create a policy as a draft: name, description, category, version, effective date, whether staff must sign it and the policy document. Publish it afterwards from the policy page.',
    sortOrder: 280,
    tabs: [],
    anchors: [
      ...common('hr.policy-create', { actions: false }),
      { id: 'hr.policy-create.form', what: 'The policy form' },
      { id: 'hr.policy-create.name', what: 'Policy Name' },
      { id: 'hr.policy-create.category-version', what: 'Category and Version' },
      { id: 'hr.policy-create.effective-date', what: 'Effective Date' },
      { id: 'hr.policy-create.requires-signature', what: 'Requires signature switch' },
      { id: 'hr.policy-create.document', what: 'Policy Document upload (PDF, Word or image)' },
      { id: 'hr.policy-create.submit', what: 'Cancel and Create Draft buttons' },
    ],
  },
  {
    pageKey: 'hr.policy',
    appKey: 'hr',
    title: 'Policy',
    route: '/app/members/admin/policies/:id',
    menuPath: 'HR › HR Operations › Policies, then open a policy',
    accessNote: HR_ADMIN,
    purpose: 'One policy: its lifecycle, audience and document. Publish & Assign sends it to every required signatory; follow assignments and signatures in the tabs. The Audit tab holds the danger zone for removing assignments or deleting the policy.',
    sortOrder: 290,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Description, quick stats and the required signatories. Default tab.' },
      { key: 'document', title: 'Document', purpose: 'The attached policy document with Preview. ?tab=document.' },
      { key: 'assignments', title: 'Assignments', purpose: 'Who the policy is assigned to, their status, due date and reminders; click someone to open their record. ?tab=assignments.' },
      { key: 'signatures', title: 'Signatures', purpose: 'Every signature recorded, newest first. ?tab=signatures.' },
      { key: 'audit', title: 'Audit', purpose: 'Lifecycle and record dates plus the danger zone. ?tab=audit.' },
    ],
    anchors: [
      ...common('hr.policy', { tabs: ['overview', 'document', 'assignments', 'signatures', 'audit'] }),
      { id: 'hr.policy.edit', what: 'Edit Policy button' },
      { id: 'hr.policy.publish', what: 'Publish & Assign button: assigns the policy to every required signatory' },
      { id: 'hr.policy.badges', what: 'Policy badges: number, version, effective, category, signature required, superseded and renewal' },
      { id: 'hr.policy.info-cards', what: 'Lifecycle, Audience and Document cards' },
      { id: 'hr.policy.tabs', what: 'The tab buttons' },
      { id: 'hr.policy.tab-overview', what: 'Overview tab button' },
      { id: 'hr.policy.tab-document', what: 'Document tab button' },
      { id: 'hr.policy.tab-assignments', what: 'Assignments tab button (count = assignments)' },
      { id: 'hr.policy.tab-signatures', what: 'Signatures tab button (count = signatures)' },
      { id: 'hr.policy.tab-audit', what: 'Audit tab button' },
      { id: 'hr.policy.overview', what: 'Description and Quick stats cards', tab: 'overview' },
      { id: 'hr.policy.signatories', what: 'Required signatories card: roles and employee types (only when set)', tab: 'overview' },
      { id: 'hr.policy.document', what: 'The attached document with Preview', tab: 'document' },
      { id: 'hr.policy.assignments', what: 'Assignment list with status, due date, signed and reminders', tab: 'assignments' },
      { id: 'hr.policy.signatures', what: 'Signature list', tab: 'signatures' },
      { id: 'hr.policy.audit', what: 'Lifecycle and Record cards', tab: 'audit' },
      { id: 'hr.policy.danger-zone', what: 'Danger zone: Remove all assignments and Delete policy', tab: 'audit' },
    ],
  },
  {
    pageKey: 'hr.policy-assignments',
    appKey: 'hr',
    title: 'Policy Assignments',
    route: '/app/members/admin/policy-assignments',
    menuPath: 'Not in the menu: HR › HR Operations › Overview, then the Policies Awaiting tile',
    accessNote: HR_ADMIN,
    purpose: 'Every policy assignment across all staff, with its status, due date and reminders sent. Run Scan marks overdue assignments and queues reminders and escalations straight away instead of waiting for the schedule.',
    sortOrder: 300,
    tabs: [],
    anchors: [
      ...common('hr.policy-assignments'),
      { id: 'hr.policy-assignments.run-scan', what: 'Run Scan button: marks overdue and sends reminders and escalations now' },
      { id: 'hr.policy-assignments.filter', what: 'Status filter and the number shown' },
      { id: 'hr.policy-assignments.table', what: 'Assignments table: user, policy, status, due, signed and reminders' },
    ],
  },
  {
    pageKey: 'hr.admin-announcements',
    appKey: 'hr',
    title: 'HR announcements',
    route: '/app/members/admin/announcements',
    menuPath: 'HR › HR Operations › Announcements',
    accessNote: HR_ADMIN,
    purpose: 'Write, publish and archive HR announcements. Publishing sends an email and push notification to every active staff member. Drafts stay private until published; archived ones can be restored.',
    sortOrder: 310,
    tabs: [],
    anchors: [
      ...common('hr.admin-announcements'),
      { id: 'hr.admin-announcements.new', what: 'New Announcement button (opens the editor)' },
      { id: 'hr.admin-announcements.stats', what: 'Total, Drafts, Published and Archived tiles (click to filter)' },
      { id: 'hr.admin-announcements.toolbar', what: 'Status chips and search' },
      { id: 'hr.admin-announcements.list', what: 'The announcements with Edit, Publish, Archive, Restore and Delete on each' },
    ],
  },

  // ---------------------------------------------------------------- HR Administration (HRAdmin)
  {
    pageKey: 'hr.final-approvals',
    appKey: 'hr',
    title: 'Final Approvals',
    route: '/app/members/admin/final-approvals',
    menuPath: 'HR › HR Administration › Final Approvals (the notification link adds ?leave={id} to open that leave)',
    accessNote: `${HR_ADMIN}. Your own leave is read only unless you are the only HR admin`,
    purpose: 'Leave that passed line approval (or has no line approver) and now waits for HR. Open a row to give final approval or reject it in the decision drawer; decided rows drop off straight away. Leave sent back to the employee waits in its own section.',
    sortOrder: 320,
    tabs: [],
    anchors: [
      ...common('hr.final-approvals'),
      { id: 'hr.final-approvals.queue', what: 'The queue (or All caught up when it is empty)' },
      { id: 'hr.final-approvals.ready', what: 'Ready for final approval list with the line approval details; click a row to decide' },
      { id: 'hr.final-approvals.to-action', what: 'The number you can act on' },
      { id: 'hr.final-approvals.waiting', what: 'Waiting on the employee list (only when there is some)' },
      { id: 'hr.final-approvals.drawer-leave', what: 'Decision drawer, The leave card: dates, days, leave type, reason and the employee comment (open a row first)' },
      { id: 'hr.final-approvals.drawer-decision', what: 'Decision drawer, Your decision card: the comment box and the action buttons (only when you can act)' },
      { id: 'hr.final-approvals.drawer-ask', what: 'Decision drawer: Ask for duty cover and Ask a question (only when asking is allowed at this step and no request is open)' },
      { id: 'hr.final-approvals.drawer-decide', what: 'Decision drawer: Reject (needs a comment) and Give final approval' },
      { id: 'hr.final-approvals.drawer-waiting', what: 'Decision drawer: the open request to the employee with Withdraw request (only while one is open)' },
      { id: 'hr.final-approvals.drawer-steps', what: 'Decision drawer, Approval steps card: the chain timeline' },
      { id: 'hr.final-approvals.drawer-context', what: 'Decision drawer: duty cover, the employee departments, leave balances and colleagues away at the same time' },
    ],
  },
  {
    pageKey: 'hr.approval-rules',
    appKey: 'hr',
    title: 'Approval Rules',
    route: '/app/members/admin/leave-approval-rules',
    menuPath: 'HR › HR Administration › Approval Rules',
    accessNote: HR_ADMIN,
    purpose: 'Who gives line approval before HR gives final approval. Department rules send leave to the head of department or named people; person rules beat department rules. Preview where a new leave would go and check the health of the rules.',
    sortOrder: 330,
    tabs: [
      { key: 'department', title: 'By department', purpose: 'Department rules: head of department or named approvers. Default tab.' },
      { key: 'person', title: 'By person', purpose: 'Rules for one employee; they beat department rules. ?tab=person.' },
      { key: 'preview', title: 'Preview chain', purpose: 'Pick an employee to see their line approvers, the rule that picked them and the HR admins. ?tab=preview.' },
      { key: 'health', title: 'Health', purpose: 'People with no line approver, head rules with no head, the HR admins and Re-apply rules to pending leave. ?tab=health.' },
    ],
    anchors: [
      ...common('hr.approval-rules', { tabs: ['department', 'person', 'preview', 'health'] }),
      { id: 'hr.approval-rules.show-off', what: 'Show switched off rules switch (By department and By person only)', tab: 'department' },
      { id: 'hr.approval-rules.add-rule', what: 'Add a rule button (By department and By person only)', tab: 'department' },
      { id: 'hr.approval-rules.tabs', what: 'The tab buttons' },
      { id: 'hr.approval-rules.tab-department', what: 'By department tab button' },
      { id: 'hr.approval-rules.tab-person', what: 'By person tab button' },
      { id: 'hr.approval-rules.tab-preview', what: 'Preview chain tab button' },
      { id: 'hr.approval-rules.tab-health', what: 'Health tab button' },
      { id: 'hr.approval-rules.department-rules', what: 'Department rules with approvers, notes, on or off, edit and switch off', tab: 'department' },
      { id: 'hr.approval-rules.person-rules', what: 'Person rules with approvers, notes, on or off, edit and switch off', tab: 'person' },
      { id: 'hr.approval-rules.preview-employee', what: 'Employee picker for the preview', tab: 'preview' },
      { id: 'hr.approval-rules.preview-chain', what: 'The previewed chain: line approval, HR final approval and their departments', tab: 'preview' },
      { id: 'hr.approval-rules.reapply', what: 'Re-apply rules to pending leave button', tab: 'health' },
      { id: 'hr.approval-rules.health-stats', what: 'Employees checked, With no line approver, Head rules with no head and HR admins tiles', tab: 'health' },
      { id: 'hr.approval-rules.no-line-approver', what: 'People with no line approver card, each with Add a rule', tab: 'health' },
    ],
  },
  {
    pageKey: 'hr.settings',
    appKey: 'hr',
    title: 'HR Settings',
    route: '/app/members/admin/settings',
    menuPath: 'HR › HR Administration › Settings',
    accessNote: HR_ADMIN,
    purpose: 'Notification defaults and leave reminders, the PaySpace connection and sync and the channels each HR notification uses. One Save button covers General and PaySpace; channel switches save straight away.',
    sortOrder: 340,
    tabs: [
      { key: 'general', title: 'General', purpose: 'Notification defaults, the leave workflow reminders and the admin email recipients. Default tab.' },
      { key: 'payspace', title: 'PaySpace', purpose: 'Connection and Test connection, sync cadence, webhooks and outbound writes and auto provisioning. ?tab=payspace.' },
      { key: 'channels', title: 'Notification Channels', purpose: 'Email, push, SMS and WhatsApp switches for every HR notification type; each switch saves straight away. ?tab=channels.' },
    ],
    anchors: [
      ...common('hr.settings', { tabs: ['general', 'payspace', 'channels'] }),
      { id: 'hr.settings.save', what: 'Save Settings button (with Reset beside it; both stay off until something changes)' },
      { id: 'hr.settings.tabs', what: 'The tab buttons' },
      { id: 'hr.settings.tab-general', what: 'General tab button' },
      { id: 'hr.settings.tab-payspace', what: 'PaySpace tab button' },
      { id: 'hr.settings.tab-channels', what: 'Notification Channels tab button (count = notification types)' },
      { id: 'hr.settings.notification-defaults', what: 'Notification Defaults card: email and push', tab: 'general' },
      { id: 'hr.settings.leave-workflow', what: 'Leave Workflow card: who is notified and the reminders', tab: 'general' },
      { id: 'hr.settings.admin-recipients', what: 'Admin Recipients card: emails for HR escalation alerts', tab: 'general' },
      { id: 'hr.settings.connection', what: 'Connection card: Enable PaySpace sync, URLs, client details and company ID', tab: 'payspace' },
      { id: 'hr.settings.test-connection', what: 'Test connection button', tab: 'payspace' },
      { id: 'hr.settings.sync-cadence', what: 'Sync Cadence card: match strategy, intervals and the lookback and lookahead days', tab: 'payspace' },
      { id: 'hr.settings.webhooks', what: 'Webhooks and Outbound card', tab: 'payspace' },
      { id: 'hr.settings.auto-provisioning', what: 'Auto-Provisioning card: create users for unmatched PaySpace employees', tab: 'payspace' },
      { id: 'hr.settings.channel-toggles', what: 'Per notification channel switches table', tab: 'channels' },
    ],
  },
  {
    pageKey: 'hr.lookups',
    appKey: 'hr',
    title: 'HR Lookups',
    route: '/app/members/admin/lookups',
    menuPath: 'HR › HR Administration › Lookups',
    accessNote: HR_ADMIN,
    purpose: 'The reference data HR uses everywhere: employee types and statuses, file types and categories, leave types, statuses and document types, the duty catalogue and policy categories. Pick a category to manage its tables.',
    sortOrder: 350,
    tabs: [],
    anchors: [
      ...common('hr.lookups', { actions: false }),
      { id: 'hr.lookups.about', what: 'About Lookup Tables note' },
      { id: 'hr.lookups.categories', what: 'Employee, Leave, Duties and Policy category cards; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.lookup-category',
    appKey: 'hr',
    title: 'Lookup category',
    route: '/app/members/admin/lookups/:category',
    menuPath: 'HR › HR Administration › Lookups, then a category (employee, leave, duties or policies)',
    accessNote: HR_ADMIN,
    purpose: 'The lookup tables in one category. Pick a table to manage its rows. The sidebar switches category.',
    sortOrder: 360,
    tabs: [],
    anchors: [
      ...common('hr.lookup-category', { actions: false }),
      { id: 'hr.lookup-category.sidebar', what: 'Sidebar: All Lookups and the categories' },
      { id: 'hr.lookup-category.tables', what: 'The lookup tables in this category; click one to open it' },
    ],
  },
  {
    pageKey: 'hr.lookup-items',
    appKey: 'hr',
    title: 'Lookup table',
    route: '/app/members/admin/lookups/:category/:lookup',
    menuPath: 'HR › HR Administration › Lookups, then a category, then a table',
    accessNote: HR_ADMIN,
    purpose: 'The rows of one lookup table, such as leave types. Add a row or edit its name, description, colour and whether it is active. Status tables use fixed ids the system relies on.',
    sortOrder: 370,
    tabs: [],
    anchors: [
      ...common('hr.lookup-items'),
      { id: 'hr.lookup-items.sidebar', what: 'Sidebar: All Lookups and the categories' },
      { id: 'hr.lookup-items.add', what: 'Add New button' },
      { id: 'hr.lookup-items.table', what: 'The rows: id, name, description, colour, status and edit' },
    ],
  },
  {
    pageKey: 'hr.payspace',
    appKey: 'hr',
    title: 'PaySpace Sync',
    route: '/app/members/admin/payspace',
    menuPath: 'HR › HR Administration › PaySpace Sync',
    accessNote: HR_ADMIN,
    purpose: 'The health of the PaySpace link: when the sync last ran, its status and how many records it synced. Trigger Sync starts a sync straight away. Connection details and timing are in HR Settings on the PaySpace tab.',
    sortOrder: 380,
    tabs: [],
    anchors: [
      ...common('hr.payspace'),
      { id: 'hr.payspace.trigger-sync', what: 'Trigger Sync button' },
      { id: 'hr.payspace.status', what: 'Last Run, Status and Records Synced' },
    ],
  },
];

// ====================================================================== Procedures (/app/procedures)

const PROC_READ = 'Admin, ProceduresAdmin or ProceduresEditor role, or the Procedures.Read right (editors see procedures they collaborate on or that belong to their department)';
const PROC_RUNS = 'Admin, ProceduresAdmin or ProceduresEditor role, or the Procedures.Runs.Read right';
const PROC_IMPORT = 'Admin, ProceduresAdmin or ProceduresEditor role, or the Procedures.Import right';
const PROC_SETTINGS = 'Admin or ProceduresAdmin role, or the Procedures.Settings.Manage right';
const PROC_EDITOR_TABS = ['details', 'steps', 'access', 'documents', 'qr', 'runs', 'questions', 'versions', 'preview'];

/** The anchors of the shared runs table (Runs page and the editor's Runs tab). */
function runAnchors(prefix: string, tab?: string): HelpAnchorDef[] {
  return [
    { id: `${prefix}.run-filters`, what: 'Run filters: search, procedure, status, channel, ministry, dates and only runs with problems', tab },
    { id: `${prefix}.export`, what: 'Export CSV button: downloads the filtered runs', tab },
    { id: `${prefix}.runs-table`, what: 'Runs table; click a run to see every step, problem and AI chat', tab },
  ];
}

const PROCEDURES_PAGES: HelpPageDef[] = [
  {
    pageKey: 'procedures.dashboard',
    appKey: 'procedures',
    title: 'Procedures dashboard',
    route: '/app/procedures',
    menuPath: 'Procedures › Overview › Dashboard',
    accessNote: PROC_READ,
    purpose: 'How procedures are being used: runs today and this week, completion rate, open problems, reviews due, AI questions and the steps where people get stuck.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('procedures.dashboard'),
      { id: 'procedures.dashboard.import', what: 'Import with AI button (needs Procedures.Import)' },
      { id: 'procedures.dashboard.new', what: 'New procedure button (needs Procedures.Manage); opens the new procedure drawer on All procedures' },
      { id: 'procedures.dashboard.stats', what: 'The stat tiles: runs, completion, problems, reviews due and AI questions' },
      { id: 'procedures.dashboard.stat-runs', what: 'Runs today tile; opens Runs' },
      { id: 'procedures.dashboard.stat-problems', what: 'Open problems tile; opens Problems' },
      { id: 'procedures.dashboard.stat-review', what: 'Due for review tile; opens All procedures filtered to reviews due' },
      { id: 'procedures.dashboard.recent-runs', what: 'Recent runs card; click a run to open its detail drawer' },
      { id: 'procedures.dashboard.problems', what: 'Open problems card; Resolve marks one resolved with a note' },
      { id: 'procedures.dashboard.review-due', what: 'Due for review card; click to open the procedure' },
      { id: 'procedures.dashboard.questions', what: 'Recent AI questions card; click to open the procedure on its Questions tab' },
      { id: 'procedures.dashboard.stall', what: 'Where people stall card: steps with problems or where runs were given up' },
    ],
  },
  {
    pageKey: 'procedures.list',
    appKey: 'procedures',
    title: 'All procedures',
    route: '/app/procedures/list',
    menuPath: 'Procedures › Procedures › All procedures',
    accessNote: PROC_READ,
    purpose: 'Every procedure you can see, with search, filters, sorting and bulk actions. Open one to edit it. ?new=1 opens the new procedure drawer.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...common('procedures.list'),
      { id: 'procedures.list.import', what: 'Import with AI button (needs Procedures.Import)' },
      { id: 'procedures.list.new', what: 'New procedure button (needs Procedures.Manage)' },
      { id: 'procedures.list.filters', what: 'Search, category, location, tag, public or private, status, review due, include archived and sort' },
      { id: 'procedures.list.table', what: 'Procedures table: tick rows for bulk actions, click a row to open the editor, ••• for row actions' },
      { id: 'procedures.list.bulk', what: 'Bulk actions for the ticked rows: archive, restore, move to category, print QR sheet (shows once rows are ticked)' },
      { id: 'procedures.list.new-drawer', what: 'New procedure drawer: title, summary, category, location, owner department, who can run it and tags' },
    ],
  },
  {
    pageKey: 'procedures.import',
    appKey: 'procedures',
    title: 'Import with AI',
    route: '/app/procedures/import',
    menuPath: 'Procedures › Procedures › Import with AI',
    accessNote: PROC_IMPORT,
    purpose: 'Upload one document, up to 20 photos or pasted text and let AI draft the procedure. You check the draft and its open questions before publishing.',
    sortOrder: 30,
    tabs: [],
    anchors: [
      ...common('procedures.import'),
      { id: 'procedures.import.ai-off', what: 'Shown when the AI import feature is switched off, with what to do instead' },
      { id: 'procedures.import.source-tabs', what: 'Upload a file or photos, or Paste text' },
      { id: 'procedures.import.dropzone', what: 'Drop zone and the picked files; photos can be dragged into page order' },
      { id: 'procedures.import.pages', what: 'Pages to use (PDF only): the pages of a manual that make up this procedure, for example 12-18, 22; blank uses every page' },
      { id: 'procedures.import.paste', what: 'Paste text box (Paste text tab)' },
      { id: 'procedures.import.options', what: 'Category and location for the new draft' },
      { id: 'procedures.import.instructions', what: 'Optional instructions for the AI' },
      { id: 'procedures.import.keep-source', what: 'Keep the source as a reference document, and whether it may answer people who are not signed in' },
      { id: 'procedures.import.start', what: 'Draft it with AI button: uploads the files and starts the import' },
      { id: 'procedures.import.progress', what: 'Progress: uploading, queued, reading, writing steps, then the editor opens' },
      { id: 'procedures.import.recent', what: 'Recent imports with their status; click one to open its draft' },
    ],
  },
  {
    pageKey: 'procedures.editor',
    appKey: 'procedures',
    title: 'Procedure editor',
    route: '/app/procedures/:id',
    menuPath: 'Procedures › Procedures › All procedures, then open a procedure',
    accessNote: `${PROC_READ}. Editing needs Procedures.Manage, publishing Procedures.Publish and access changes need Owner or full access`,
    purpose: 'Everything about one procedure: details, steps, who can run it, reference documents for the AI chat, QR poster, runs, questions, versions and a phone preview.',
    sortOrder: 40,
    tabs: [
      { key: 'details', title: 'Details', purpose: 'Title, summary, category, location, owner department, who can run it, review cycle, AI chat, tags and cover image. Default tab.' },
      { key: 'steps', title: 'Steps', purpose: 'Before you start notes and media, then the steps: title, where, what to do, sub-checks, tips and warnings, photos, video and files. One Save. ?tab=steps.' },
      { key: 'access', title: 'Access', purpose: 'Public or private, the audience of a private procedure (people, roles, departments, ministries) and collaborators. ?tab=access.' },
      { key: 'documents', title: 'Reference documents', purpose: 'Files the AI chat answers from, public or private, with their reading status. ?tab=documents.' },
      { key: 'qr', title: 'QR and print', purpose: 'The QR code, its link, scans, a new QR code, PNG download and the A5 poster. ?tab=qr.' },
      { key: 'runs', title: 'Runs', purpose: 'Every run of this procedure and the run detail. Needs Procedures.Runs.Read. ?tab=runs.' },
      { key: 'questions', title: 'Questions', purpose: 'What people asked the AI chat, and whether it could answer. Needs Procedures.Runs.Read. ?tab=questions.' },
      { key: 'versions', title: 'Versions', purpose: 'Published versions with change notes; restore one into the draft. ?tab=versions.' },
      { key: 'preview', title: 'Preview', purpose: 'The saved draft as runners see it, in a phone frame. ?tab=preview.' },
    ],
    anchors: [
      ...common('procedures.editor', { tabs: PROC_EDITOR_TABS }),
      { id: 'procedures.editor.publish', what: 'Publish button: makes the saved draft live with a change note (Procedures.Publish)' },
      { id: 'procedures.editor.open-portal', what: 'Open in portal button (published procedures only)' },
      { id: 'procedures.editor.header', what: 'Title with status, public or private, version and unpublished changes badges' },
      { id: 'procedures.editor.open-questions', what: 'Open questions from the AI import, with Dismiss (only after an AI import)' },
      { id: 'procedures.editor.tabs', what: 'The tab buttons' },
      ...PROC_EDITOR_TABS.map((t) => ({ id: `procedures.editor.tab-${t}`, what: `${t.charAt(0).toUpperCase()}${t.slice(1)} tab button` })),
      { id: 'procedures.editor.details-form', what: 'The details form', tab: 'details' },
      { id: 'procedures.editor.details-title', what: 'Title field', tab: 'details' },
      { id: 'procedures.editor.details-category', what: 'Category picker', tab: 'details' },
      { id: 'procedures.editor.details-visibility', what: 'Who can run it: Public or Private', tab: 'details' },
      { id: 'procedures.editor.details-ai-chat', what: 'AI chat switches', tab: 'details' },
      { id: 'procedures.editor.details-cover', what: 'Cover image picker (from the procedure photos)', tab: 'details' },
      { id: 'procedures.editor.before-you-start', what: 'Before you start: safety notes, prerequisites and overview media', tab: 'steps' },
      { id: 'procedures.editor.procedure-media', what: 'Overview photos, video and files above step 1', tab: 'steps' },
      { id: 'procedures.editor.steps-list', what: 'The steps; drag the handle to reorder, click a step to open it', tab: 'steps' },
      { id: 'procedures.editor.add-step', what: 'Add a step button', tab: 'steps' },
      { id: 'procedures.editor.visibility', what: 'Public or Private switch (saves straight away)', tab: 'access' },
      { id: 'procedures.editor.audience', what: 'Audience of a private procedure: people, roles, departments and ministries', tab: 'access' },
      { id: 'procedures.editor.collaborators', what: 'Collaborators with Editor or Owner level', tab: 'access' },
      { id: 'procedures.editor.documents-upload', what: 'Upload a reference document, public or private', tab: 'documents' },
      { id: 'procedures.editor.documents-list', what: 'Reference documents with reading status, public switch, download, reindex and delete', tab: 'documents' },
      { id: 'procedures.editor.qr-code', what: 'The QR code and its scans', tab: 'qr' },
      { id: 'procedures.editor.qr-actions', what: 'Download PNG and New QR code', tab: 'qr' },
      { id: 'procedures.editor.qr-link', what: 'The link the QR code opens, with Copy', tab: 'qr' },
      { id: 'procedures.editor.poster', what: 'A5 poster preview and Print poster', tab: 'qr' },
      ...runAnchors('procedures.editor', 'runs'),
      { id: 'procedures.editor.questions', what: 'Questions people asked the AI chat', tab: 'questions' },
      { id: 'procedures.editor.versions', what: 'Published versions; Restore to draft', tab: 'versions' },
      { id: 'procedures.editor.preview-phone', what: 'The phone preview of the saved draft', tab: 'preview' },
    ],
  },
  {
    pageKey: 'procedures.runs',
    appKey: 'procedures',
    title: 'Runs',
    route: '/app/procedures/runs',
    menuPath: 'Procedures › Activity › Runs',
    accessNote: PROC_RUNS,
    purpose: 'Every time someone worked through a procedure: who, when, how far they got, problems and AI chat. Filter, export to CSV and open a run for detail.',
    sortOrder: 50,
    tabs: [],
    anchors: [
      ...common('procedures.runs'),
      ...runAnchors('procedures.runs'),
      { id: 'procedures.runs.detail', what: 'Run detail drawer: runner, ministry, channel, version, timings, each step, problem resolution and AI chat (opens when you click a run)' },
    ],
  },
  {
    pageKey: 'procedures.problems',
    appKey: 'procedures',
    title: 'Problems',
    route: '/app/procedures/problems',
    menuPath: 'Procedures › Activity › Problems',
    accessNote: `${PROC_RUNS}. Resolving needs Procedures.Manage`,
    purpose: 'Steps people flagged as a problem during a run. Resolve them with a note, or open the run or the steps.',
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...common('procedures.problems'),
      { id: 'procedures.problems.tabs', what: 'Open and Resolved' },
      { id: 'procedures.problems.list', what: 'The problems; Resolve, See the run and Open the steps on each' },
    ],
  },
  {
    pageKey: 'procedures.qr-sheet',
    appKey: 'procedures',
    title: 'QR sheet',
    route: '/app/procedures/qr-sheet',
    menuPath: 'Procedures › Procedures › QR sheet',
    accessNote: PROC_READ,
    purpose: 'Printable QR cards for a category, a location or picked procedures (published only), six per A4 page. Takes ?categoryId=, ?locationId= and ?ids=.',
    sortOrder: 70,
    tabs: [],
    anchors: [
      ...common('procedures.qr-sheet'),
      { id: 'procedures.qr-sheet.print', what: 'Print button' },
      { id: 'procedures.qr-sheet.filters', what: 'Category and location, and the picked procedures from All procedures' },
      { id: 'procedures.qr-sheet.cards', what: 'The QR cards as they print' },
    ],
  },
  {
    pageKey: 'procedures.categories',
    appKey: 'procedures',
    title: 'Categories',
    route: '/app/procedures/categories',
    menuPath: 'Procedures › Administration › Categories',
    accessNote: `${PROC_READ}. Changes need ${PROC_SETTINGS}`,
    purpose: 'The category tree procedures are filed under in the console and the portal directory.',
    sortOrder: 80,
    tabs: [],
    anchors: [
      ...common('procedures.categories'),
      { id: 'procedures.categories.new', what: 'New category button' },
      { id: 'procedures.categories.tree', what: 'The categories as a tree, with procedures, QR sheet, add sub-category, edit and delete' },
      { id: 'procedures.categories.drawer', what: 'Category drawer: inside, name, description, icon, colour, sort order and active' },
    ],
  },
  {
    pageKey: 'procedures.settings',
    appKey: 'procedures',
    title: 'Procedures settings',
    route: '/app/procedures/settings',
    menuPath: 'Procedures › Administration › Settings',
    accessNote: PROC_SETTINGS,
    purpose: 'Portal name and link, public runs, sign-in, AI chat limits, notifications, review cycle and run retention, plus the status of the AI and media services.',
    sortOrder: 90,
    tabs: [],
    anchors: [
      ...common('procedures.settings'),
      { id: 'procedures.settings.status', what: 'AI import, AI chat and media storage status, with what to do when one is off' },
      { id: 'procedures.settings.portal', what: 'Portal: name, link, welcome and support text, public runs, sign-in and the robot check' },
      { id: 'procedures.settings.ai', what: 'AI chat for people not signed in, and the question limits' },
      { id: 'procedures.settings.notifications', what: 'Problem alerts to owners and access granted messages' },
      { id: 'procedures.settings.retention', what: 'Default review cycle, when idle runs are given up and how long runs are kept' },
    ],
  },
];

// ====================================================================== Call campaigns (/app/callcentre)

const CC_READ = 'Admin, CallCentreAdmin or CallCentreSupervisor role, or the CallCentre.Read right';
const CC_CAMPAIGNS = 'Admin, CallCentreAdmin or CallCentreSupervisor role, or the CallCentre.Campaigns.Manage right';
const CC_SETTINGS = 'Admin or CallCentreAdmin role, or the CallCentre.Settings.Manage right';
const CC_TELEPHONY = 'Admin or CallCentreAdmin role, or the CallCentre.Telephony.Manage right';

/** The anchors of the shared list review (Import review page and wizard step 3). */
function reviewAnchors(prefix: string): HelpAnchorDef[] {
  return [
    { id: `${prefix}.counts`, what: 'The list name, its status and the count chips (All, Matched, Possible, New, Duplicate, Invalid, Undecided); a chip filters the rows' },
    { id: `${prefix}.bulk`, what: 'Bulk decisions: take the best match for all possible rows, create members for all new rows, skip all of a kind (Import right, list ready for review)' },
    { id: `${prefix}.rows`, what: 'The rows: person, status, CCMS match (pick a candidate, create a new member or skip), Planning Centre status and the original cells' },
    { id: `${prefix}.commit`, what: 'Add to campaign with "Assign to callers straight away", and Cancel this list' },
  ];
}

const CALLCENTRE_PAGES: HelpPageDef[] = [
  {
    pageKey: 'callcentre.dashboard',
    appKey: 'callcentre',
    title: 'Call campaigns dashboard',
    route: '/app/callcentre',
    menuPath: 'Call campaigns › Overview › Dashboard',
    accessNote: CC_READ,
    purpose: 'The call centre at a glance: live campaigns, people waiting for a caller, calls and reach rate, handoffs, detail changes, lists to review and your reminders.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('callcentre.dashboard'),
      { id: 'callcentre.dashboard.new', what: 'New campaign button (needs CallCentre.Campaigns.Manage); opens the wizard' },
      { id: 'callcentre.dashboard.stats', what: 'The KPI tiles: active campaigns, open contacts, not assigned, calls today, reach rate, open handoffs, detail changes and lists waiting' },
      { id: 'callcentre.dashboard.stat-campaigns', what: 'Active campaigns tile; opens Campaigns filtered to Active' },
      { id: 'callcentre.dashboard.stat-unassigned', what: 'Not assigned tile; opens Contacts filtered to people without a caller' },
      { id: 'callcentre.dashboard.stat-handoffs', what: 'Open handoffs tile; opens Handoffs' },
      { id: 'callcentre.dashboard.campaigns', what: 'Active campaigns card: progress of each live campaign; click one to open it' },
      { id: 'callcentre.dashboard.reminders', what: 'Reminders card: your own reminders; click one to open where it points, the cross dismisses it' },
      { id: 'callcentre.dashboard.calls-by-day', what: 'Calls per day chart for the last 14 days' },
      { id: 'callcentre.dashboard.outcomes', what: 'Outcomes this week chart' },
      { id: 'callcentre.dashboard.funnel', what: 'Journey funnel chart: completed, booked and no-shows per step (DA, E1 to E5)' },
      { id: 'callcentre.dashboard.top-callers', what: 'Top callers this week' },
      { id: 'callcentre.dashboard.live', what: 'Live card (realtime): who is on a call now and for how long, calls and people reached today, callers on shift; "Every minute" when the live connection is down' },
      { id: 'callcentre.dashboard.ai-themes', what: 'Themes from recent calls (AI; only when the CallCentreInsights flag and the AI switch are on): the period (7, 14 or 30 days), Refresh, the themes, prayer themes and coaching hints, plus how many notes it read' },
    ],
  },
  {
    pageKey: 'callcentre.campaigns',
    appKey: 'callcentre',
    title: 'Campaigns',
    route: '/app/callcentre/campaigns',
    menuPath: 'Call campaigns › Calling › Campaigns',
    accessNote: CC_READ,
    purpose: 'Every call campaign with its status, progress, people not assigned and callers. ?status= presets the status filter.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...common('callcentre.campaigns'),
      { id: 'callcentre.campaigns.new', what: 'New campaign button (needs CallCentre.Campaigns.Manage)' },
      { id: 'callcentre.campaigns.filters', what: 'Search, status and Include archived' },
      { id: 'callcentre.campaigns.table', what: 'The campaigns table; click a row to open the campaign' },
    ],
  },
  {
    pageKey: 'callcentre.campaign-wizard',
    appKey: 'callcentre',
    title: 'New campaign',
    route: '/app/callcentre/campaigns/new',
    menuPath: 'Call campaigns › Calling › Campaigns › New campaign (a draft: open it, then Continue setting up)',
    accessNote: `${CC_CAMPAIGNS}. Adding people needs CallCentre.Import; callers need CallCentre.Assign`,
    purpose: 'Set up a campaign in five steps: details, add people from a spreadsheet, review the CCMS matches, callers, launch. Resumable at /app/callcentre/campaigns/:id/setup?step=0 to 4.',
    sortOrder: 30,
    tabs: [],
    anchors: [
      ...common('callcentre.campaign-wizard'),
      { id: 'callcentre.campaign-wizard.steps', what: 'The five steps; once the draft exists you can click any step' },
      { id: 'callcentre.campaign-wizard.details', what: 'Step 1: type (fills in the script), name, goal, description, dates, campus, colour, owner and ministry' },
      { id: 'callcentre.campaign-wizard.script', what: 'Step 1: the call script with merge-field chips' },
      { id: 'callcentre.campaign-wizard.ai-draft', what: 'Step 1: Draft with AI under the script (only when the CallCentreScriptAssist flag and the AI switch are on); the draft opens in a box with Use this draft' },
      { id: 'callcentre.campaign-wizard.rules', what: 'Step 1: calling rules (most tries, wait between tries, cool-down); blank uses the default' },
      { id: 'callcentre.campaign-wizard.assignment', what: 'Step 1: detail-change mode and how people are shared out' },
      { id: 'callcentre.campaign-wizard.save-details', what: 'Step 1: Create the draft and continue (or Save and continue)' },
      { id: 'callcentre.campaign-wizard.spreadsheet', what: 'Step 2: pick a CSV or Excel file' },
      { id: 'callcentre.campaign-wizard.template', what: 'Step 2: CSV and Excel templates whose columns map by themselves' },
      { id: 'callcentre.campaign-wizard.mapping', what: 'Step 2: match the columns to first name, surname, full name, phone, email and note' },
      { id: 'callcentre.campaign-wizard.ai-mapping', what: 'Step 2: Suggest mapping (AI; only when the CallCentreImportAssist flag and the AI switch are on): fills only the columns still Not in the file, with a confidence badge' },
      { id: 'callcentre.campaign-wizard.preview', what: 'Step 2: the first rows and how many have no phone or name' },
      { id: 'callcentre.campaign-wizard.upload', what: 'Step 2: Upload button' },
      { id: 'callcentre.campaign-wizard.progress', what: 'Step 2: upload and matching progress (pauses with Try again when the member service is busy)' },
      { id: 'callcentre.campaign-wizard.sources', what: 'Step 2: where the people come from (spreadsheet, CCMS members, Planning Centre List, Planning Centre form, CCMS form or event sign-ups, Acts Care registrations)' },
      { id: 'callcentre.campaign-wizard.source-pco-form', what: 'Step 2, Planning Centre form: search and pick a form (Include archived forms shows older ones); the badge is its submissions' },
      { id: 'callcentre.campaign-wizard.source-pco-form-since', what: 'Step 2, Planning Centre form: Submitted since (optional) and the count of people the pull brings in, with a phone and already CCMS members; each person comes in once' },
      ...reviewAnchors('callcentre.campaign-wizard'),
      { id: 'callcentre.campaign-wizard.add-callers', what: 'Step 4: add callers from your callers or any CCMS user' },
      { id: 'callcentre.campaign-wizard.caller-list', what: 'Step 4: the callers on this campaign with their caps' },
      { id: 'callcentre.campaign-wizard.save-callers', what: 'Step 4: Save callers' },
      { id: 'callcentre.campaign-wizard.share-out', what: 'Step 4: Share out evenly (balanced assignment of the people not assigned yet)' },
      { id: 'callcentre.campaign-wizard.launch', what: 'Step 5: the ready check' },
      { id: 'callcentre.campaign-wizard.launch-button', what: 'Step 5: Launch (or Keep it as a draft)' },
    ],
  },
  {
    pageKey: 'callcentre.campaign-detail',
    appKey: 'callcentre',
    title: 'Campaign',
    route: '/app/callcentre/campaigns/:id',
    menuPath: 'Call campaigns › Calling › Campaigns, then open a campaign',
    accessNote: `${CC_READ}. Status changes and edits need CallCentre.Campaigns.Manage; callers and assigning need CallCentre.Assign; adding people needs CallCentre.Import; export needs CallCentre.Contacts.Export`,
    purpose: 'One campaign: counts and progress, its people, callers and workload, script and questions plus the lists added to it. Status moves follow Draft, Active, Paused, Completed and Archived.',
    sortOrder: 40,
    tabs: [
      { key: 'overview', title: 'Overview', purpose: 'Counts, progress, details and the rules in force. Default tab.' },
      { key: 'contacts', title: 'Contacts', purpose: 'The people on the campaign: filters, assign or release in bulk, add a person, export, contact drawer. ?tab=contacts.' },
      { key: 'callers', title: 'Callers and workload', purpose: 'Each caller\'s open and done people and calls; Balance, Rebalance, Reassign, Release, Add a team and Manage callers, plus the share-out options (only this team, by campus). ?tab=callers.' },
      { key: 'script', title: 'Script and questions', purpose: 'The call script with merge fields and the custom questions. ?tab=script.' },
      { key: 'guide', title: 'Conversation guide', purpose: 'An overview of the step-by-step guide callers follow beside the script: its steps, version, calls that followed it and when it was last changed, a small picture of the flow, Open the builder (the full-screen guide builder) and Build with AI, then the report of how calls moved through it. ?tab=guide.' },
      { key: 'results', title: 'Results and messages', purpose: 'Which call results callers on this campaign see under How did the call go? (Shown switch, at least one stays) and, under each result, the messages that go to the person after it by email, SMS and / or WhatsApp: start from the library or write your own with merge fields, Callers can untick it, Switched on, Add suggested messages (added switched off). Nobody on do not call is messaged; only email after a wrong number; messages wait for calling hours. ?tab=results.' },
      { key: 'lists', title: 'Lists', purpose: 'Every list added, with its matches; Add more people. ?tab=lists.' },
      { key: 'pco', title: 'Planning Centre', purpose: 'Link everyone on the campaign to Planning Centre (create missing CCMS members, find or create and link) and follow the linking jobs, plus What callers can update (opens Settings, Fields callers can update). ?tab=pco.' },
    ],
    anchors: [
      ...common('callcentre.campaign-detail', { tabs: ['overview', 'contacts', 'callers', 'script', 'guide', 'results', 'lists', 'pco'] }),
      { id: 'callcentre.campaign-detail.results', what: 'Call results with their Shown switch and the messages under each', tab: 'results' },
      { id: 'callcentre.campaign-detail.suggested', what: 'Add suggested messages button', tab: 'results' },
      { id: 'callcentre.campaign-detail.messages-off', what: 'Warning that messages to people are switched off in Settings', tab: 'results' },
      { id: 'callcentre.campaign-detail.pco-fields', what: 'What callers can update, with Choose fields', tab: 'pco' },
      { id: 'callcentre.campaign-detail.badges', what: 'Status and type badges with the number of people and progress' },
      { id: 'callcentre.campaign-detail.status-launch', what: 'Launch button (draft campaigns)' },
      { id: 'callcentre.campaign-detail.status-pause', what: 'Pause button (live campaigns)' },
      { id: 'callcentre.campaign-detail.status-resume', what: 'Resume button (paused campaigns)' },
      { id: 'callcentre.campaign-detail.status-complete', what: 'Complete button (live or paused campaigns)' },
      { id: 'callcentre.campaign-detail.edit', what: 'Edit details button' },
      { id: 'callcentre.campaign-detail.setup', what: 'Draft banner with Continue setting up' },
      { id: 'callcentre.campaign-detail.tabs', what: 'The tabs' },
      { id: 'callcentre.campaign-detail.tab-overview', what: 'Overview tab button' },
      { id: 'callcentre.campaign-detail.tab-contacts', what: 'Contacts tab button' },
      { id: 'callcentre.campaign-detail.tab-callers', what: 'Callers and workload tab button' },
      { id: 'callcentre.campaign-detail.tab-script', what: 'Script and questions tab button' },
      { id: 'callcentre.campaign-detail.tab-lists', what: 'Lists tab button' },
      { id: 'callcentre.campaign-detail.tab-pco', what: 'Planning Centre tab button' },
      { id: 'callcentre.campaign-detail.tab-guide', what: 'Conversation guide tab button' },
      { id: 'callcentre.campaign-detail.guide', what: 'Conversation guide card: the guide name, Callers see this guide (or Hidden from callers), Open the builder, Build with AI and Replace with the starter guide (or, with no guide yet, Open the builder, Copy the starter guide and Build with AI)', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-open', what: 'Open the builder: the full-screen conversation guide builder (View the guide without CallCentre.Campaigns.Manage)', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-copy', what: 'Copy the starter guide button (only when the campaign has no guide and its type has a starter guide)', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-ai', what: 'Build with AI: opens the builder with the Build with AI dialog (only when the CallCentreGuideBuilder flag and the AI switch are on)', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-summary', what: 'The guide in numbers: steps (and endings), the version, calls that followed it (all versions and this one) and when and by whom it was last edited', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-preview', what: 'A small read-only picture of the flow (drag to look around); change it in the builder', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-report', what: 'Guide report: calls that used the guide, reached an End, and per step the reach and the split per answer and per rule; for Input steps what callers typed (counts per value, lowest, highest and average numbers, calls per day for dates)', tab: 'guide' },
      { id: 'callcentre.campaign-detail.guide-paths', what: 'Recent calls with the path each one took', tab: 'guide' },
      { id: 'callcentre.campaign-detail.counts', what: 'Count tiles', tab: 'overview' },
      { id: 'callcentre.campaign-detail.live', what: 'Live card for this campaign (only while it is active): callers on a call now, calls and reaches today', tab: 'overview' },
      { id: 'callcentre.campaign-detail.progress', what: 'Progress bar by status', tab: 'overview' },
      { id: 'callcentre.campaign-detail.about', what: 'About card: goal, dates, owner, ministry', tab: 'overview' },
      { id: 'callcentre.campaign-detail.rules', what: 'Rules in force', tab: 'overview' },
      { id: 'callcentre.campaign-detail.filters', what: 'Contact filters, Refresh, Export CSV and Add person', tab: 'contacts' },
      { id: 'callcentre.campaign-detail.table', what: 'Contacts table; tick rows to assign or release, click a row for the contact drawer', tab: 'contacts' },
      { id: 'callcentre.campaign-detail.add-person', what: 'Add person button', tab: 'contacts' },
      { id: 'callcentre.campaign-detail.export', what: 'Export CSV button', tab: 'contacts' },
      { id: 'callcentre.campaign-detail.workload-actions', what: 'Balance, Rebalance, Reassign, Add a team and Manage callers', tab: 'callers' },
      { id: 'callcentre.campaign-detail.add-team', what: 'Add a team button: every active caller in a team joins the campaign (needs CallCentre.Assign)', tab: 'callers' },
      { id: 'callcentre.campaign-detail.balance-options', what: 'Share-out options for Balance and Rebalance: Only this team, By campus and Leave the rest unassigned (needs CallCentre.Assign)', tab: 'callers' },
      { id: 'callcentre.campaign-detail.workload', what: 'Workload table per caller', tab: 'callers' },
      { id: 'callcentre.campaign-detail.script', what: 'Call script editor', tab: 'script' },
      { id: 'callcentre.campaign-detail.ai-draft', what: 'Draft with AI under the script (only when the CallCentreScriptAssist flag and the AI switch are on); Save script saves a draft you used', tab: 'script' },
      { id: 'callcentre.campaign-detail.questions', what: 'Questions editor', tab: 'script' },
      { id: 'callcentre.campaign-detail.add-more', what: 'Add more people button', tab: 'lists' },
      { id: 'callcentre.campaign-detail.lists', what: 'Lists table; click one to review it', tab: 'lists' },
      { id: 'callcentre.campaign-detail.pco-link', what: 'Link to Planning Centre card: create CCMS members for people without one, and find or create them in Planning Centre and link (needs CallCentre.Import)', tab: 'pco' },
      { id: 'callcentre.campaign-detail.pco-link-button', what: 'Link to Planning Centre button', tab: 'pco' },
      { id: 'callcentre.campaign-detail.pco-jobs-card', what: 'Planning Centre jobs card: linking and detail updates for this campaign', tab: 'pco' },
      { id: 'callcentre.campaign-detail.pco-jobs', what: 'Jobs table with progress, failed and conflict counts; click a job for its people, Retry failed or Cancel', tab: 'pco' },
    ],
  },
  {
    pageKey: 'callcentre.guide-builder',
    appKey: 'callcentre',
    title: 'Conversation guide builder',
    route: '/app/callcentre/campaigns/:id/guide',
    menuPath: 'Call campaigns › Calling › Campaigns › a campaign › Conversation guide › Open the builder (starter guides: Settings › Campaign types › Edit guide)',
    accessNote: `${CC_READ} (starter guides: the Settings page). Editing needs CallCentre.Campaigns.Manage; Build with AI also needs its AI flag and the AI switch`,
    purpose: 'Build the step-by-step conversation guide callers follow on the call screen, full screen: boxes and arrows on a canvas, a + on every answer to add the next step, ready-made blocks, the step panel, Try it, Undo and Redo, Build with AI (Anthropic Claude on Amazon Bedrock) and Save. Unsaved changes are kept on this device.',
    sortOrder: 41,
    tabs: [],
    anchors: [
      ...common('callcentre.guide-builder', { actions: false }),
      { id: 'callcentre.guide-builder.top', what: 'The top bar: back, the guide name, the version line, Callers see it, Undo and Redo, Try it, AI, Discard and Save' },
      { id: 'callcentre.guide-builder.back', what: 'Back to the campaign (or to Settings, Campaign types); unsaved changes stay on this device' },
      { id: 'callcentre.guide-builder.version', what: 'The version line: the version, how many calls followed it and whether saving makes a new version (moving boxes never does)' },
      { id: 'callcentre.guide-builder.active', what: 'Callers see it: off hides the guide from callers (the script still shows); campaign guides only' },
      { id: 'callcentre.guide-builder.undo', what: 'Undo and Redo (Ctrl+Z and Ctrl+Shift+Z)' },
      { id: 'callcentre.guide-builder.try', what: 'Try it: walk the guide with sample names and a sample person, unsaved changes included' },
      { id: 'callcentre.guide-builder.guide-try-person', what: 'Try it: the person on the call (member or not, campus, journey steps, tries, list source, campaign type, member fields); changing it starts again' },
      { id: 'callcentre.guide-builder.ai', what: 'Build with AI (an empty guide) or the AI menu: Ask AI to change this guide and Build a new guide with AI. Only when Build with AI is on' },
      { id: 'callcentre.guide-builder.ai-dialog', what: 'Build with AI: what the call should achieve, who is being called, the tone, questions it must ask, what to stay away from and how long; the AI drafts the whole guide and nothing is saved' },
      { id: 'callcentre.guide-builder.ai-banner', what: 'AI draft banner: check every step before saving, what the AI did, things to check and Undo the AI draft' },
      { id: 'callcentre.guide-builder.discard', what: 'Discard: back to the saved guide (only with unsaved changes)' },
      { id: 'callcentre.guide-builder.save', what: 'Save (or Create guide); the red number is how many things to fix first (Ctrl+S)' },
      { id: 'callcentre.guide-builder.empty', what: 'An empty guide: Start from the starter, Build with AI, Start from scratch or a ready-made block' },
      { id: 'callcentre.guide-builder.canvas', what: 'The canvas: a box per step (Say, Ask, Input, Check, End in their own colour and icon) and an arrow per answer, rule, Next or Otherwise; the + on a row adds the next step, inserts one in between, adds a ready-made block or goes to a step already there; drag boxes to move them, drag from a dot to connect' },
      { id: 'callcentre.guide-builder.palette', what: 'The palette: click or drag Say, Ask, Input, Check or End onto the canvas (a click adds it after the selected step); fold it away with the arrow' },
      { id: 'callcentre.guide-builder.blocks', what: 'Ready-made blocks: Is now a good time?, Offer a callback, Invite to Discover Acts, Prayer request and Thank and close' },
      { id: 'callcentre.guide-builder.toolbar', what: 'Arrange, Fit, Report numbers (campaign guides) and the keyboard shortcuts' },
      { id: 'callcentre.guide-builder.arrange', what: 'Arrange: lay the boxes out again from the start step (positions never make a new version)' },
      { id: 'callcentre.guide-builder.report', what: 'Report numbers: show on each arrow how many calls went that way (every version or one version)' },
      { id: 'callcentre.guide-builder.shortcuts', what: 'Keyboard shortcuts: Undo, Redo, Save, Esc, Delete, F to fit and 1 to 5 to add a step' },
      { id: 'callcentre.guide-builder.problems', what: 'Things to fix (or Every step checks out): the list of problems with Show me, which opens the step' },
      { id: 'callcentre.guide-builder.inspector', what: 'The step panel over the canvas: what the caller says, what happens next (the way on, answers, what the caller types, rules or the result), How callers see this step and More options' },
      { id: 'callcentre.guide-builder.quick-answers', what: 'Yes / No and True / False: add those answers to an Ask step in one click' },
      { id: 'callcentre.guide-builder.rules', what: 'Rules of a Check (or, under More options, an Input) step, first match wins: what it checks, the test, the value and where the call goes' },
      { id: 'callcentre.guide-builder.suggested', what: 'Suggested result of an End step (preselected for the caller, who still confirms it)' },
      { id: 'callcentre.guide-builder.preview', what: 'How callers see this step: the step as the call screen shows it, with sample names' },
    ],
  },
  {
    pageKey: 'callcentre.import-review',
    appKey: 'callcentre',
    title: 'Review the list',
    route: '/app/callcentre/imports/:batchId',
    menuPath: 'Call campaigns › Calling › Campaigns, open a campaign, Lists tab, then a list',
    accessNote: `${CC_READ}. Decisions and adding need CallCentre.Import`,
    purpose: 'Check who on an uploaded list matched a CCMS member, decide the possible matches, then add the people to the campaign.',
    sortOrder: 50,
    tabs: [],
    anchors: [...common('callcentre.import-review'), ...reviewAnchors('callcentre.import-review')],
  },
  {
    pageKey: 'callcentre.contacts',
    appKey: 'callcentre',
    title: 'Contacts',
    route: '/app/callcentre/contacts',
    menuPath: 'Call campaigns › Calling › Contacts',
    accessNote: `${CC_READ}. Bulk assign needs CallCentre.Assign; export needs CallCentre.Contacts.Export; edits need CallCentre.Contacts.Edit`,
    purpose: 'Everyone on every campaign, with search and filters. /app/callcentre/contacts/:id opens a contact straight away.',
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...common('callcentre.contacts'),
      { id: 'callcentre.contacts.filters', what: 'Search, campaign, status, caller, last outcome, Still to call only, Refresh and Export CSV (with a campaign picked)' },
      { id: 'callcentre.contacts.table', what: 'Contacts table; click a row for the contact drawer' },
      { id: 'callcentre.contacts.bulk', what: 'Assign to caller and Release to the pool for the ticked rows (a campaign must be picked)' },
      { id: 'callcentre.contacts.export', what: 'Export CSV button (a campaign must be picked)' },
    ],
  },
  {
    pageKey: 'callcentre.do-not-call',
    appKey: 'callcentre',
    title: 'Do not call',
    route: '/app/callcentre/do-not-call',
    menuPath: 'Call campaigns › Calling › Do not call',
    accessNote: `${CC_READ}. Adding and removing need CallCentre.Contacts.Edit`,
    purpose: 'Numbers nobody in the organisation may call on any campaign.',
    sortOrder: 70,
    tabs: [],
    anchors: [
      ...common('callcentre.do-not-call'),
      { id: 'callcentre.do-not-call.add', what: 'Add a number button' },
      { id: 'callcentre.do-not-call.table', what: 'The list: last four digits, person, reason, who added it and when; Remove' },
    ],
  },
  {
    pageKey: 'callcentre.detail-changes',
    appKey: 'callcentre',
    title: 'Detail changes',
    route: '/app/callcentre/detail-changes',
    menuPath: 'Call campaigns › Calling › Detail changes',
    accessNote: `${CC_READ}. Approving and rejecting need CallCentre.Contacts.Edit`,
    purpose: 'Details callers added or corrected on calls (names, contact details and the member fields allowed in Settings), waiting for review before they update the CCMS member. Approving checks the Member fields settings again: a field switched off since fails with the reason, and an Add if missing change is Skipped when the member has a value by then.',
    sortOrder: 80,
    tabs: [],
    anchors: [
      ...common('callcentre.detail-changes'),
      { id: 'callcentre.detail-changes.table', what: 'The changes: person, the detail with its kind (built-in field, contact list use or custom field) and Add if missing, old and new value, who proposed it, the status (Pending, Applied, Rejected, Failed or Skipped, with the reason) and Planning Centre; tick rows to review them' },
      { id: 'callcentre.detail-changes.bulk', what: 'Approve and Reject for the ticked rows' },
    ],
  },
  {
    pageKey: 'callcentre.journey',
    appKey: 'callcentre',
    title: 'Journey',
    route: '/app/callcentre/journey',
    menuPath: 'Call campaigns › Follow-up › Journey',
    accessNote: `${CC_READ}. Steps (and their Acts Care and Planning Centre links) need CallCentre.Settings.Manage; recording and Back-fill from Acts Care need CallCentre.Journey.Manage`,
    purpose: "The church journey (Discover Acts and E1 to E5): the funnel, recent entries with their Planning Centre status, the step set-up (an Acts Care course completes a step by itself; Planning Centre gets a custom field date or a workflow card) and recording or back-filling a step for a member.",
    sortOrder: 90,
    tabs: [
      { key: 'board', title: 'Board', purpose: 'Funnel, the per-step table and recent entries. Default tab.' },
      { key: 'steps', title: 'Steps', purpose: 'Code, name, colour, icon, order, booking target (pick an Acts Care course to complete the step automatically) and Planning Centre sync (custom field or workflow card) per step, plus Back-fill from Acts Care. ?tab=steps.' },
      { key: 'record', title: 'Record a step', purpose: 'Pick a member and record, correct or back-fill a step. ?tab=record.' },
    ],
    anchors: [
      ...common('callcentre.journey', { tabs: ['board', 'steps', 'record'] }),
      { id: 'callcentre.journey.tabs', what: 'The tabs' },
      { id: 'callcentre.journey.tab-board', what: 'Board tab button' },
      { id: 'callcentre.journey.tab-steps', what: 'Steps tab button' },
      { id: 'callcentre.journey.tab-record', what: 'Record a step tab button (Journey.Manage)' },
      { id: 'callcentre.journey.funnel', what: 'Funnel chart', tab: 'board' },
      { id: 'callcentre.journey.by-step', what: 'Per-step table', tab: 'board' },
      { id: 'callcentre.journey.recent', what: 'Recent entries, each with its Planning Centre status (Sent, Waiting or Not linked to Planning Centre) and source', tab: 'board' },
      { id: 'callcentre.journey.integrations', what: 'What the links do: Acts Care courses complete steps by themselves, Planning Centre gets the completion', tab: 'steps' },
      { id: 'callcentre.journey.add-step', what: 'Add step button', tab: 'steps' },
      { id: 'callcentre.journey.steps', what: 'Steps table with the booking target and Planning Centre sync; click a step to edit it (booking target type, Acts Care course, Planning Centre custom field or workflow card)', tab: 'steps' },
      { id: 'callcentre.journey.backfill', what: 'Back-fill from Acts Care on a step row (Journey.Manage; only for steps linked to an Acts Care course): pick a since year, then confirm', tab: 'steps' },
      { id: 'callcentre.journey.record', what: 'Member picker, their badges so far and the record form', tab: 'record' },
    ],
  },
  {
    pageKey: 'callcentre.handoffs',
    appKey: 'callcentre',
    title: 'Handoffs',
    route: '/app/callcentre/handoffs',
    menuPath: 'Call campaigns › Follow-up › Handoffs',
    accessNote: `${CC_READ}. Status changes, reassigning, Send to Acts Care and ministry owners need CallCentre.Handoffs.Manage`,
    purpose: 'People handed on to Care, a ministry or someone else, with where they went (Acts Care, added to the ministry) and which were escalated. Also who owns each ministry\'s handoffs.',
    sortOrder: 100,
    tabs: [
      { key: 'handoffs', title: 'Handoffs', purpose: 'Filter, change status (Joined adds the person to the ministry), reassign, send Care referrals to Acts Care; click a row for the contact. Default tab.' },
      { key: 'owners', title: 'Ministry owners', purpose: 'Who receives each ministry\'s handoffs. ?tab=owners.' },
    ],
    anchors: [
      ...common('callcentre.handoffs', { tabs: ['handoffs', 'owners'] }),
      { id: 'callcentre.handoffs.tabs', what: 'The tabs' },
      { id: 'callcentre.handoffs.tab-handoffs', what: 'Handoffs tab button' },
      { id: 'callcentre.handoffs.tab-owners', what: 'Ministry owners tab button' },
      { id: 'callcentre.handoffs.filters', what: 'Status, type, campaign, ministry and Open only', tab: 'handoffs' },
      { id: 'callcentre.handoffs.table', what: 'Handoffs table with the Escalated badge and where the referral went (Acts Care CR-..., Added to the ministry); ••• for Change status, Send to Acts Care and Reassign', tab: 'handoffs' },
      { id: 'callcentre.handoffs.handover-result', what: 'What the last Send to Acts Care or Joined did (shows after one of those)', tab: 'handoffs' },
      { id: 'callcentre.handoffs.add-owner', what: 'Add owner button', tab: 'owners' },
      { id: 'callcentre.handoffs.owners', what: 'Ministry owners table', tab: 'owners' },
    ],
  },
  {
    pageKey: 'callcentre.callers',
    appKey: 'callcentre',
    title: 'Callers',
    route: '/app/callcentre/callers',
    menuPath: 'Call campaigns › People and results › Callers',
    accessNote: `${CC_READ}. Adding, editing and removing callers and teams need CallCentre.Assign`,
    purpose: 'Everyone set up as a caller, with their roles and activity; add a caller and give them portal access. Also caller teams with a campus and a lead.',
    sortOrder: 110,
    tabs: [
      { key: 'callers', title: 'Callers', purpose: 'Everyone set up as a caller; Add caller, Edit and Remove. Default tab.' },
      { key: 'teams', title: 'Teams', purpose: 'Caller teams with a campus, a lead and members; New team, Edit and Delete. A campaign can add a team or share people out to one team or by campus. ?tab=teams.' },
    ],
    anchors: [
      ...common('callcentre.callers', { tabs: ['callers', 'teams'] }),
      { id: 'callcentre.callers.tabs', what: 'The tabs' },
      { id: 'callcentre.callers.tab-callers', what: 'Callers tab button' },
      { id: 'callcentre.callers.tab-teams', what: 'Teams tab button' },
      { id: 'callcentre.callers.add', what: 'Add caller button', tab: 'callers' },
      { id: 'callcentre.callers.table', what: 'Callers table with roles, campaigns, open people, calls and last seen; Edit and Remove', tab: 'callers' },
      { id: 'callcentre.callers.add-team', what: 'New team button', tab: 'teams' },
      { id: 'callcentre.callers.teams', what: 'Teams table with campus, lead and members; Edit and Delete', tab: 'teams' },
    ],
  },
  {
    pageKey: 'callcentre.reports',
    appKey: 'callcentre',
    title: 'Reports',
    route: '/app/callcentre/reports',
    menuPath: 'Call campaigns › People and results › Reports',
    accessNote: `${CC_READ}. The CSV export needs CallCentre.Contacts.Export`,
    purpose: 'Calls, reach and outcomes over a period, by caller, campaign, outcome and day.',
    sortOrder: 120,
    tabs: [],
    anchors: [
      ...common('callcentre.reports'),
      { id: 'callcentre.reports.export', what: 'Export calls (CSV) button' },
      { id: 'callcentre.reports.filters', what: 'From and to dates, campaign, caller and the 7, 30 and 90 day shortcuts' },
      { id: 'callcentre.reports.totals', what: 'Totals tiles' },
      { id: 'callcentre.reports.by-caller', what: 'By caller table' },
      { id: 'callcentre.reports.by-campaign', what: 'By campaign table' },
    ],
  },
  {
    pageKey: 'callcentre.settings',
    appKey: 'callcentre',
    title: 'Call centre settings',
    route: '/app/callcentre/settings',
    menuPath: 'Call campaigns › Administration › Settings',
    accessNote: CC_SETTINGS,
    purpose: 'Calling hours and days, campaign defaults, reminders, notifications, the Acts Care and ministry integrations, AI assist, privacy and the portal, the member fields callers can change, plus the outcomes and campaign types.',
    sortOrder: 130,
    tabs: [
      { key: 'general', title: 'General', purpose: 'Hours, defaults, when reminders are due, privacy and the portal. Default tab.' },
      { key: 'notifications', title: 'Notifications', purpose: 'The master switch with email, SMS and WhatsApp, the daily summary, escalation to supervisors, the topics table and Send me a test. ?tab=notifications.' },
      { key: 'integrations', title: 'Integrations', purpose: 'Send Care referrals to Acts Care (with the Care need), add people to the ministry when a referral is marked Joined and AI assist (the AI switch and what each helper does). ?tab=integrations.' },
      { key: 'member-fields', title: 'Fields callers can update', purpose: 'Which member fields callers may change on a call for people linked to a CCMS member: built-in fields, contact list uses (Calling, WhatsApp, SMS, Email), custom fields and Planning Centre fields (only for people linked to Planning Centre, written to Planning Centre; checkbox and file fields are not offered), each with Can add if missing (fills an empty field only, never overwrites) and Can update. Sensitive custom fields are never offered. Saved on its own with Save member fields; read-only while Member Management cannot be reached. Changes follow the detail change mode on the General tab. ?tab=member-fields.' },
      { key: 'messages', title: 'Messages', purpose: 'Messages to the people called: the master switch (saved with the Save bar) and the message library campaigns start from (built-in messages can be edited or hidden, your own also deleted). ?tab=messages.' },
      { key: 'outcomes', title: 'Outcomes', purpose: 'The outcomes callers pick, with group, flags (The number is wrong: no SMS or WhatsApp after it) and shortcut key. A campaign picks which ones its callers see. ?tab=outcomes.' },
      { key: 'types', title: 'Campaign types', purpose: 'Campaign types with their default script and starter conversation guide. ?tab=types.' },
    ],
    anchors: [
      ...common('callcentre.settings', { tabs: ['general', 'notifications', 'integrations', 'member-fields', 'messages', 'outcomes', 'types'] }),
      { id: 'callcentre.settings.tab-messages', what: 'Messages tab button' },
      { id: 'callcentre.settings.notes', what: 'A note on every call switch', tab: 'general' },
      { id: 'callcentre.settings.person-messages', what: 'Send messages after calls (master switch) and the safeguards', tab: 'messages' },
      { id: 'callcentre.settings.message-library', what: 'Message library with Add message', tab: 'messages' },
      { id: 'callcentre.settings.tabs', what: 'The tabs' },
      { id: 'callcentre.settings.tab-general', what: 'General tab button' },
      { id: 'callcentre.settings.tab-notifications', what: 'Notifications tab button' },
      { id: 'callcentre.settings.tab-integrations', what: 'Integrations tab button' },
      { id: 'callcentre.settings.tab-member-fields', what: 'Fields callers can update tab button' },
      { id: 'callcentre.settings.tab-outcomes', what: 'Outcomes tab button' },
      { id: 'callcentre.settings.tab-types', what: 'Campaign types tab button' },
      { id: 'callcentre.settings.hours', what: 'Calling hours and days', tab: 'general' },
      { id: 'callcentre.settings.defaults', what: 'Campaign defaults', tab: 'general' },
      { id: 'callcentre.settings.reminders', what: 'When reminders are due: callbacks, close your call, untouched people, campaign ending and handoff waiting', tab: 'general' },
      { id: 'callcentre.settings.privacy', what: 'Phone masking, caller export and retention', tab: 'general' },
      { id: 'callcentre.settings.portal', what: 'Portal link and welcome text', tab: 'general' },
      { id: 'callcentre.settings.channels', what: 'The master switch with the Email, SMS and WhatsApp switches under it', tab: 'notifications' },
      { id: 'callcentre.settings.channel-switches', what: 'The Email, SMS and WhatsApp switches', tab: 'notifications' },
      { id: 'callcentre.settings.digest', what: 'Daily summary, its hour and when referrals escalate to supervisors', tab: 'notifications' },
      { id: 'callcentre.settings.topics', what: 'Topics table: email, SMS and WhatsApp per kind of message, with Save topics', tab: 'notifications' },
      { id: 'callcentre.settings.topics-save', what: 'Save topics button', tab: 'notifications' },
      { id: 'callcentre.settings.test', what: 'Send me a test: pick channels, see what happened', tab: 'notifications' },
      { id: 'callcentre.settings.care', what: 'Send Care referrals to Acts Care and the Acts Care need', tab: 'integrations' },
      { id: 'callcentre.settings.ministry', what: 'Add people to the ministry when a referral is marked Joined', tab: 'integrations' },
      { id: 'callcentre.settings.ai', what: 'AI assist: the Use AI assist switch, what each helper does, its AI feature flag and what is sent to AWS Bedrock', tab: 'integrations' },
      { id: 'callcentre.settings.member-fields', what: 'Member fields callers can change: what the two switches do, how many fields are offered, how many sensitive fields are never offered, and Discard and Save member fields', tab: 'member-fields' },
      { id: 'callcentre.settings.member-fields-save', what: 'Save member fields button (only the switches you changed are saved)', tab: 'member-fields' },
      { id: 'callcentre.settings.member-fields-unavailable', what: 'Member Management cannot be reached: the saved fields are listed read-only, with Try again', tab: 'member-fields' },
      { id: 'callcentre.settings.member-fields-1', what: 'Built-in fields table (first name, surname, date of birth, gender, email, cell number and more) with Can add if missing and Can update', tab: 'member-fields' },
      { id: 'callcentre.settings.member-fields-2', what: 'Contact list table: the main Calling, WhatsApp, SMS and Email entries with Can add if missing and Can update', tab: 'member-fields' },
      { id: 'callcentre.settings.member-fields-3', what: 'Custom fields table (fields added under Member fields, never the sensitive ones) with Can add if missing and Can update', tab: 'member-fields' },
      { id: 'callcentre.settings.add-outcome', what: 'Add outcome button', tab: 'outcomes' },
      { id: 'callcentre.settings.outcomes', what: 'Outcomes table; click one to edit it', tab: 'outcomes' },
      { id: 'callcentre.settings.add-type', what: 'Add type button', tab: 'types' },
      { id: 'callcentre.settings.types', what: 'Campaign types table; click one to edit it', tab: 'types' },
      { id: 'callcentre.settings.type-guide', what: 'Starter guide button per campaign type (Add a guide or Edit guide): opens the full-screen guide builder for the guide new campaigns of the type copy (editing needs CallCentre.Campaigns.Manage)', tab: 'types' },
    ],
  },
  {
    pageKey: 'callcentre.telephony',
    appKey: 'callcentre',
    title: 'Phone system',
    route: '/app/callcentre/telephony',
    menuPath: 'Call campaigns › Administration › Phone system',
    accessNote: `${CC_READ}. Changing the settings, Test, Diagnose, Read call records now and the desk stations need ${CC_TELEPHONY}`,
    purpose: "Click-to-call through the church's Grandstream UCM: the connection settings and checks, the desk phones callers pick for a shift and the call records read back from the UCM. /app/callcentre/stations opens the Desk stations tab.",
    sortOrder: 140,
    tabs: [
      { key: 'connection', title: 'Connection', purpose: 'Status, the UCM settings (write-only password), Test and Diagnose, and what IT sets up on the UCM. Default tab.' },
      { key: 'stations', title: 'Desk stations', purpose: 'Desk phones with their extension and who is using each now; add, edit, switch off or on. ?tab=stations.' },
      { key: 'calls', title: 'Call records', purpose: 'Call records read from the UCM and whether each matched a call; Read call records now. ?tab=calls.' },
    ],
    anchors: [
      ...common('callcentre.telephony', { tabs: ['connection', 'stations', 'calls'] }),
      { id: 'callcentre.telephony.tabs', what: 'The tabs' },
      { id: 'callcentre.telephony.tab-connection', what: 'Connection tab button' },
      { id: 'callcentre.telephony.tab-stations', what: 'Desk stations tab button' },
      { id: 'callcentre.telephony.tab-calls', what: 'Call records tab button' },
      { id: 'callcentre.telephony.status', what: 'Status card: phone system on or off, the last test and when call records were last read', tab: 'connection' },
      { id: 'callcentre.telephony.settings', what: 'Connection to the UCM: on or off, address, API user, password (leave blank to keep the saved one), certificate, outbound prefix, national format, time zone and reading call records', tab: 'connection' },
      { id: 'callcentre.telephony.check', what: 'Check the connection card with Test and Diagnose (Telephony.Manage)', tab: 'connection' },
      { id: 'callcentre.telephony.test', what: 'Test button: signs in to the UCM with the saved settings', tab: 'connection' },
      { id: 'callcentre.telephony.diagnose', what: 'Diagnose button: name lookup, network, certificate, API and sign in, step by step', tab: 'connection' },
      { id: 'callcentre.telephony.check-result', what: 'The check result: each step green or red with its message and time', tab: 'connection' },
      { id: 'callcentre.telephony.it-checklist', what: 'What IT sets up on the UCM: the API on port 8089, the API user, allowing 13.246.64.102, outbound route rights, caller ID and extensions', tab: 'connection' },
      { id: 'callcentre.telephony.add-station', what: 'Add desk phone button (Telephony.Manage)', tab: 'stations' },
      { id: 'callcentre.telephony.stations', what: 'Desk phones table: name, extension, campus and who is using it now; click one to edit, Switch off or Switch on', tab: 'stations' },
      { id: 'callcentre.telephony.cdr-filters', what: 'Not matched to a call only, when records were last read, Refresh and Read call records now', tab: 'calls' },
      { id: 'callcentre.telephony.cdr-sync', what: 'Read call records now button (Telephony.Manage)', tab: 'calls' },
      { id: 'callcentre.telephony.cdr', what: 'Call records table: started, from, to (masked), result, talk time and whether it matched a call', tab: 'calls' },
    ],
  },
];

// ====================================================================== Caller portal (callcentre.actschurch.co.za)
//
// The volunteers' portal is its own site (frontend/ccms_callcentre), so these pages have NO console route (route ''):
// console Ask AI must never offer a "Go to" link into it, and the help editor's preview cannot open it (take the
// tour in the portal instead, from its ? menu). The portal maps its routes to these page keys itself
// (src/components/help/helpPages.ts); keep both lists in step. One ? per screen, in the portal's top bar.
// Anchors `{pageKey}.help`, `.nav` (sidebar on wide screens, bottom tab bar on phones) and `.reminders` (the bell)
// are in the frame on every page; the rest are inside the page. Steps on an element that is missing (no data, or a
// phone layout that shows it later) fall back to a centred card. Never point a step into a drawer or a dialog.

const CCP_ACCESS =
  'Callers (CallCentreCaller role or the CallCentre.Calls.Make right) and call centre staff (Admin, CallCentreAdmin or CallCentreSupervisor role, or the CallCentre.Read right)';

/** The frame anchors every portal page has: the ? button, the navigation and the reminders bell. */
function portalFrame(pageKey: string): HelpAnchorDef[] {
  return [
    { id: `${pageKey}.help`, what: 'The ? help button in the top bar (Take the tour, Help for this page, Reset tours, the auto-start switch)' },
    { id: `${pageKey}.nav`, what: 'The navigation: the black sidebar on wide screens, the bottom bar (Home, My list, Call, More) on phones' },
    { id: `${pageKey}.reminders`, what: 'The reminders bell in the top bar, with a dot for unread reminders' },
  ];
}

const CALLCENTRE_PORTAL_PAGES: HelpPageDef[] = [
  {
    pageKey: 'callcentre-portal.home',
    appKey: 'callcentre-portal',
    title: 'Home',
    route: '',
    menuPath: 'Caller portal › Home',
    accessNote: CCP_ACCESS,
    purpose: "The caller's calling plan for today: the numbers, Start calling, the shift (when the phone system is on), my campaigns and my callbacks. The portal's start page (/).",
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.home'),
      { id: 'callcentre-portal.home.shift', what: 'The shift card: pick a desk phone or your own extension and Start shift, or End shift (only when the phone system is on)' },
      { id: 'callcentre-portal.home.stats', what: 'Today at a glance: people in my list, callbacks due, calls today and people reached' },
      { id: 'callcentre-portal.home.start', what: 'The green "Make your next connection" card with Start calling' },
      { id: 'callcentre-portal.home.campaigns', what: 'My campaigns: each campaign with open and done, Call this list, View list and Take the next people from the pool' },
      { id: 'callcentre-portal.home.callbacks', what: 'Callbacks due: the next few people who asked for a callback' },
    ],
  },
  {
    pageKey: 'callcentre-portal.call',
    appKey: 'callcentre-portal',
    title: 'Call screen',
    route: '',
    menuPath: 'Caller portal › Call',
    accessNote: CCP_ACCESS,
    purpose: 'One person at a time: their card and the Call button, the conversation guide, how the call went, notes, questions, journey steps, referrals and detail changes, then Save. Portal paths /call and /call/{contact}. On phones the call is two steps: the call and its result, then Continue to the rest.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.call'),
      { id: 'callcentre-portal.call.person', what: 'The person card: campaign, tries, name, number, where they came from and the Call button' },
      { id: 'callcentre-portal.call.call-button', what: 'The Call button (the desk phone rings first when the caller is on a shift; otherwise it opens the phone app)' },
      { id: 'callcentre-portal.call.script', what: 'The campaign script and goal (titled Call script when the campaign also has a step-by-step guide; only when the campaign has a script)' },
      { id: 'callcentre-portal.call.guide', what: 'The step-by-step conversation guide above the results: the words to say, answer buttons, a field or Yes and No buttons on Input steps (the value is saved with the call), a short line when a check went on by itself and why ("They have completed Discover Acts, so going to ..."), the answers so far, Back (walks back past checks) and Start again; an End step preselects its suggested result (only when the campaign has a guide)' },
      { id: 'callcentre-portal.call.history', what: 'Earlier calls (only when the person was called before)' },
      { id: 'callcentre-portal.call.outcomes', what: 'How did the call go? The outcome buttons' },
      { id: 'callcentre-portal.call.notes', what: 'Notes with the "Pastoral note (hidden from other callers)" box (on phones only after Continue)' },
      { id: 'callcentre-portal.call.follow-up', what: 'The follow-up sections together: campaign questions, journey and next step, care and ministry referral, update details (on phones only after Continue)' },
      { id: 'callcentre-portal.call.questions', what: 'Campaign questions (only when the campaign has questions; on phones after Continue)' },
      { id: 'callcentre-portal.call.journey', what: 'Journey and next step: record DA or E1 to E5, book an Acts Care course date (on phones after Continue)' },
      { id: 'callcentre-portal.call.referral', what: 'Care and ministry referral (needs their agreement; on phones after Continue)' },
      { id: 'callcentre-portal.call.details', what: 'Update details: for people linked to a CCMS member, their member details with Add or Change (the fields allowed in Settings, Member fields; never sensitive ones); otherwise a new name, cell number or email (on phones after Continue)' },
      { id: 'callcentre-portal.call.actions', what: 'The bar at the bottom: Skip, Give back and Save & next person on wide screens; Continue, then Save & next person and Save & finish on phones' },
      { id: 'callcentre-portal.call.skip', what: "Phones only: Can't talk to them now? with Skip and Give back" },
    ],
  },
  {
    pageKey: 'callcentre-portal.list',
    appKey: 'callcentre-portal',
    title: 'My list',
    route: '',
    menuPath: 'Caller portal › My list',
    accessNote: CCP_ACCESS,
    purpose: 'Everyone the caller is calling, grouped in call order (callbacks due, new, try again, later), with search and filters. Portal path /list.',
    sortOrder: 30,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.list'),
      { id: 'callcentre-portal.list.filters', what: 'Search, the campaign filter (with more than one campaign) and the last outcome filter' },
      { id: 'callcentre-portal.list.groups', what: 'The chips: All, Callbacks due, New, Try again and Later, with counts' },
      { id: 'callcentre-portal.list.people', what: 'The people table (or "Your list is empty"); tap a person to open their call' },
      { id: 'callcentre-portal.list.next-step', what: 'Your next step: how many are ready, Call next person and the call window' },
    ],
  },
  {
    pageKey: 'callcentre-portal.callbacks',
    appKey: 'callcentre-portal',
    title: 'Callbacks',
    route: '',
    menuPath: 'Caller portal › Callbacks',
    accessNote: CCP_ACCESS,
    purpose: 'Every callback the caller promised: due now first, then coming up. Portal path /callbacks.',
    sortOrder: 40,
    tabs: [],
    anchors: [...portalFrame('callcentre-portal.callbacks'), { id: 'callcentre-portal.callbacks.list', what: 'The callbacks (Due now, Coming up) or "No callbacks"' }],
  },
  {
    pageKey: 'callcentre-portal.referrals',
    appKey: 'callcentre-portal',
    title: 'Referrals',
    route: '',
    menuPath: 'Caller portal › Referrals',
    accessNote: 'Ministry owners, and anyone with an open referral',
    purpose: 'People a caller referred to a ministry the person looks after: accept, follow up, mark as done or decline. Portal path /referrals.',
    sortOrder: 50,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.referrals'),
      { id: 'callcentre-portal.referrals.counts', what: 'Awaiting acceptance, In progress and Completed counts' },
      { id: 'callcentre-portal.referrals.tabs', what: 'The chips: To review, In progress, Completed (and Declined when there are any)' },
      { id: 'callcentre-portal.referrals.inbox', what: 'The referrals in the chosen chip, newest or oldest first (or the empty message)' },
      { id: 'callcentre-portal.referrals.detail', what: 'The chosen referral: contact details, note, timeline and the next step (Accept, Mark as done, Decline)' },
    ],
  },
  {
    pageKey: 'callcentre-portal.reminders',
    appKey: 'callcentre-portal',
    title: 'Reminders',
    route: '',
    menuPath: 'Caller portal › Reminders',
    accessNote: CCP_ACCESS,
    purpose: "The caller's own reminders: callbacks due, calls to finish, campaigns ending. Open, mark as read or dismiss. Portal path /reminders.",
    sortOrder: 60,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.reminders'),
      { id: 'callcentre-portal.reminders.show-read', what: "The \"Show reminders I've already read\" switch" },
      { id: 'callcentre-portal.reminders.list', what: 'The reminders with Open, Mark as read and Dismiss (or "No reminders")' },
    ],
  },
  {
    pageKey: 'callcentre-portal.stats',
    appKey: 'callcentre-portal',
    title: 'My stats',
    route: '',
    menuPath: 'Caller portal › My stats',
    accessNote: CCP_ACCESS,
    purpose: "The caller's numbers for today, this week or all time, calls per day this week and how calls ended. Portal path /stats.",
    sortOrder: 70,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.stats'),
      { id: 'callcentre-portal.stats.period', what: 'Today, This week or All time (after the first call)' },
      { id: 'callcentre-portal.stats.numbers', what: 'Calls made, people reached, connection rate and next steps booked (or "No calls yet")' },
      { id: 'callcentre-portal.stats.week', what: 'Calls this week, a bar per day' },
      { id: 'callcentre-portal.stats.outcomes', what: 'Call outcomes this week and referrals passed on' },
    ],
  },
  {
    pageKey: 'callcentre-portal.settings',
    appKey: 'callcentre-portal',
    title: 'Settings',
    route: '',
    menuPath: 'Caller portal › Settings',
    accessNote: CCP_ACCESS,
    purpose: "The caller's name and phone number, reminders (email, SMS, WhatsApp, daily summary), quiet hours, appearance and sign out. Portal path /settings.",
    sortOrder: 80,
    tabs: [],
    anchors: [
      ...portalFrame('callcentre-portal.settings'),
      { id: 'callcentre-portal.settings.about', what: 'About you: your name and phone number' },
      { id: 'callcentre-portal.settings.reminders', what: 'Reminders: Email, SMS, WhatsApp and Daily summary' },
      { id: 'callcentre-portal.settings.quiet', what: 'Quiet hours: From and Until' },
      { id: 'callcentre-portal.settings.save', what: 'Save settings' },
      { id: 'callcentre-portal.settings.appearance', what: 'Appearance: Match my device, Light or Dark' },
      { id: 'callcentre-portal.settings.sign-out', what: 'Sign out' },
    ],
  },
];

// ====================================================================== Members (/admin/members)

const MEMBERS_PAGES: HelpPageDef[] = [
  {
    pageKey: 'members.custom-fields',
    appKey: 'members',
    title: 'Member fields',
    route: '/admin/members/custom-fields',
    menuPath: 'Admin › Members › Member fields (the button at the top of the member directory; also Manage fields under More details on a member\'s Contact tab)',
    accessNote: 'The Admin role or the Member.CustomFields.Manage right. Filling fields in on a member needs member edit rights (Admin, HRAdmin or Member.Write).',
    purpose: 'Define extra member fields once for all of CCMS (for example an alternative WhatsApp number or a baptism date): type, choices, category, several values, sensitive, on or off and the order. Members only show the fields that have a value, on the Contact tab under More details.',
    sortOrder: 10,
    tabs: [],
    anchors: [
      ...common('members.custom-fields'),
      { id: 'members.custom-fields.new', what: 'Add a field button: label, key (made from the label when left blank, never changes), type, choices, category, help text, several values, sensitive and on' },
      { id: 'members.custom-fields.toolbar', what: 'Search by label, key or category; the switch shows fields that are switched off' },
      { id: 'members.custom-fields.list', what: 'The fields in display order: move up or down, type, category, how many members have a value, the On switch, Edit and Delete (only while no member has a value)' },
    ],
  },
  {
    pageKey: 'members.member',
    appKey: 'members',
    title: 'Member profile',
    route: '/admin/members/:id',
    menuPath: 'Admin › Members, then open a member',
    accessNote: 'The Admin role or the Member.Read right. Changes need the Admin role or Member.Write; Family, Notes, Activity and the Admin tab actions have their own rights.',
    purpose: 'One member record by MemberID (visitors and children without a login too): personal details, contact details and more details (custom member fields), addresses, health, family, campus, ministries and roles, emergency contacts, plus notes, links, activity and admin actions. Opens tabs with ?tab= (personal is the default).',
    sortOrder: 20,
    tabs: [
      { key: 'personal', title: 'Personal', purpose: 'Names, date of birth, gender, marital status and the hide my birthday switch. Default tab (no ?tab=).' },
      { key: 'contact', title: 'Contact', purpose: 'Contact details (phone numbers and emails with a label, uses Calling, WhatsApp, SMS and Email and one main per use; the main Calling number and main email are the member\'s cell number and email) and More details (custom member fields with a value, Add a field, Manage fields). ?tab=contact.' },
      { key: 'addresses', title: 'Addresses', purpose: 'Add, edit and remove addresses. ?tab=addresses.' },
      { key: 'health', title: 'Health', purpose: 'Allergies and disabilities, each with an optional note. ?tab=health.' },
      { key: 'family', title: 'Family', purpose: 'The households the member belongs to and the people in them; with Member.Family.Write, create a household, add people and change relationships. ?tab=family.' },
      { key: 'campus', title: 'Campus', purpose: 'Home campus and the campus they serve at. ?tab=campus.' },
      { key: 'ministries', title: 'Ministries and roles', purpose: 'Ministries, departments and designations (read only; changed in Departments). ?tab=ministries.' },
      { key: 'emergency', title: 'Emergency contacts', purpose: 'Who to call in an emergency, with one primary contact. ?tab=emergency.' },
      { key: 'notes', title: 'Notes', purpose: 'Pastoral and admin notes, never shown to the member. ?tab=notes.' },
      { key: 'links', title: 'Links', purpose: 'The login, Planning Centre and Anatomy links. ?tab=links.' },
      { key: 'activity', title: 'Activity', purpose: 'Every change to the record, field by field. ?tab=activity.' },
      { key: 'admin', title: 'Admin', purpose: 'Membership status, type and member code, merge another record into this one, delete and restore. ?tab=admin.' },
    ],
    anchors: [
      ...common('members.member', { actions: false }),
      { id: 'members.member.header', what: 'Profile header: photo, name, member code, type and status badges, Refresh' },
      { id: 'members.member.completeness', what: 'Profile complete meter with what is still missing' },
      { id: 'members.member.tabs', what: 'The tab buttons (scroll sideways on small screens)' },
      { id: 'members.member.tab-personal', what: 'Personal tab button' },
      { id: 'members.member.tab-contact', what: 'Contact tab button (Contact details and More details)' },
      { id: 'members.member.tab-addresses', what: 'Addresses tab button' },
      { id: 'members.member.tab-health', what: 'Health tab button' },
      { id: 'members.member.tab-family', what: 'Family tab button (with a family right)' },
      { id: 'members.member.tab-campus', what: 'Campus tab button' },
      { id: 'members.member.tab-ministries', what: 'Ministries and roles tab button' },
      { id: 'members.member.tab-emergency', what: 'Emergency contacts tab button' },
      { id: 'members.member.tab-notes', what: 'Notes tab button (with the notes right)' },
      { id: 'members.member.tab-links', what: 'Links tab button' },
      { id: 'members.member.tab-activity', what: 'Activity tab button (with the audit right)' },
      { id: 'members.member.tab-admin', what: 'Admin tab button' },
    ],
  },
];

// ====================================================================== Communications (/admin/communications)

const WA_MANAGE = 'The Admin role or Communications.WhatsApp.View to look; Refresh, linking and New template need Communications.WhatsApp.Manage';

const COMMUNICATIONS_PAGES: HelpPageDef[] = [
  {
    pageKey: 'communications.whatsapp-templates',
    appKey: 'communications',
    title: 'WhatsApp templates',
    route: '/admin/communications/whatsapp/templates',
    menuPath: 'Admin › Communications › WhatsApp › Library › Templates',
    accessNote: WA_MANAGE,
    purpose: 'The templates Meta approved for the church WhatsApp number (synced from 360dialog every 30 minutes) and which CCMS message each one sends: status, rejection reasons, a phone preview, and links from messages to approved templates. ?tab=templates (default) or ?tab=messages.',
    sortOrder: 10,
    tabs: [
      { key: 'templates', title: 'Templates', purpose: 'Every template on the account with status chips, filters and a details drawer with a phone preview. ?tab=templates (default).' },
      { key: 'messages', title: 'CCMS messages', purpose: 'Every CCMS message (any module) with its WhatsApp link: Link or Change opens the link editor, Create opens the template creator. ?tab=messages.' },
    ],
    anchors: [
      ...common('communications.whatsapp-templates'),
      { id: 'communications.whatsapp-templates.refresh', what: 'Refresh: read every template from 360dialog now (Manage only)' },
      { id: 'communications.whatsapp-templates.new', what: 'New template: open the assisted template creator (Manage only)' },
      { id: 'communications.whatsapp-templates.tabs', what: 'The Templates and CCMS messages tabs' },
      { id: 'communications.whatsapp-templates.status-chips', what: 'Status chips with counts: All, Approved, Pending, Rejected, Paused (click one to filter)', tab: 'templates' },
      { id: 'communications.whatsapp-templates.filters', what: 'Search, category, language, Linked only and Deleted', tab: 'templates' },
      { id: 'communications.whatsapp-templates.table', what: 'The templates: name and wording, language, category, status (hover a rejected one for the reason), quality and the messages that use it; click a row for the details drawer', tab: 'templates' },
      { id: 'communications.whatsapp-templates.message-filters', what: 'Search messages and pick a module', tab: 'messages' },
      { id: 'communications.whatsapp-templates.messages', what: 'The CCMS messages with their CCMS texts and WhatsApp link state, with Link or Change and Create', tab: 'messages' },
    ],
  },
  {
    pageKey: 'communications.whatsapp-template-creator',
    appKey: 'communications',
    title: 'New WhatsApp template',
    route: '/admin/communications/whatsapp/templates/new',
    menuPath: 'Admin › Communications › WhatsApp › Templates › New template (or Create next to a message; Forms settings › Messages › Create)',
    accessNote: 'The Admin role or Communications.WhatsApp.Manage. Suggest wording also needs the WhatsAppTemplateWording AI flag',
    purpose: 'Write a WhatsApp template step by step (message, category, name, wording with field chips and samples, buttons), check it against Meta\'s rules and submit it to Meta. It links itself to its message once approved. ?message={TemplateID} picks the message; ?step=1..5 opens a step.',
    sortOrder: 20,
    tabs: [],
    anchors: [
      ...common('communications.whatsapp-template-creator'),
      { id: 'communications.whatsapp-template-creator.steps', what: 'The steps: Message, Category, Name, Wording, Check and submit (click one to go there)' },
      { id: 'communications.whatsapp-template-creator.message', what: 'Step 1: the CCMS message it is for, with its fields (route ?step=1)' },
      { id: 'communications.whatsapp-template-creator.category', what: 'Step 2: Utility, Marketing or Authentication with guidance (route ?step=2)' },
      { id: 'communications.whatsapp-template-creator.name', what: 'Step 3: the template name (lower_snake_case) and language (route ?step=3)' },
      { id: 'communications.whatsapp-template-creator.wording', what: 'Step 4: header, message, fields, samples, footer and buttons (route ?step=4)' },
      { id: 'communications.whatsapp-template-creator.use-suggested', what: 'Use suggested wording (messages CCMS has a starter for, such as the Forms messages; step 4)' },
      { id: 'communications.whatsapp-template-creator.ai', what: 'Suggest wording with Claude (only when the AI flag is on; step 4)' },
      { id: 'communications.whatsapp-template-creator.fields', what: 'The field chips: tap one to put {{n}} where the cursor is (step 4)' },
      { id: 'communications.whatsapp-template-creator.samples', what: 'Fields and samples: which field fills each {{n}} and the sample Meta reviews (step 4)' },
      { id: 'communications.whatsapp-template-creator.buttons', what: 'Buttons: Link (with a changing part), Quick reply, Call (step 4)' },
      { id: 'communications.whatsapp-template-creator.check', what: 'Step 5: the rule checker (errors and warnings), the summary and the link switch (route ?step=5)' },
      { id: 'communications.whatsapp-template-creator.submit', what: 'Submit to Meta (only when there are no errors; step 5)' },
      { id: 'communications.whatsapp-template-creator.preview', what: 'The phone preview with the samples, and the error and warning counts' },
      { id: 'communications.whatsapp-template-creator.guidance', what: 'What a good WhatsApp message includes' },
    ],
  },
];

// ====================================================================== Forms (/app/forms)

const FORMS_PAGES: HelpPageDef[] = [
  {
    pageKey: 'forms.settings',
    appKey: 'forms',
    title: 'Forms settings',
    route: '/app/forms/admin/settings',
    menuPath: 'Forms › Administration › Module Settings',
    accessNote: 'The Admin or FormsAdmin role, or the Forms.Settings.Manage right',
    purpose: 'Switches and defaults for every form: notification channels, the Messages section (how each Forms message goes out, and its WhatsApp template), integrations, limits and the public base URL.',
    sortOrder: 90,
    tabs: [],
    anchors: [
      ...common('forms.settings'),
      { id: 'forms.settings.notifications', what: 'Notifications: the master switch and the Email, SMS, WhatsApp and Push channels' },
      { id: 'forms.settings.messages', what: 'Messages: the nine Forms messages with Email, SMS and WhatsApp for each' },
      { id: 'forms.settings.messages-table', what: 'The messages table: link state and template, Link or Change (the link editor) and Create (the template creator)' },
    ],
  },
];

export const HELP_REGISTRY: HelpPageDef[] = [...EVENTS_PAGES, ...RIDES_PAGES, ...HR_PAGES, ...PROCEDURES_PAGES, ...CALLCENTRE_PAGES, ...CALLCENTRE_PORTAL_PAGES, ...MEMBERS_PAGES, ...COMMUNICATIONS_PAGES, ...FORMS_PAGES];

const byKey = new Map(HELP_REGISTRY.map((p) => [p.pageKey, p] as const));

export function getHelpPageDef(pageKey: string): HelpPageDef | undefined {
  return byKey.get(pageKey);
}

export function helpPagesForApp(appKey: string | null | undefined): HelpPageDef[] {
  return HELP_REGISTRY.filter((p) => !appKey || p.appKey === appKey).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function helpAnchorsFor(pageKey: string): HelpAnchorDef[] {
  return byKey.get(pageKey)?.anchors ?? [];
}
